import { customAlphabet } from "nanoid";
import * as env from "$app/env/public";
import { 
  ROLES_AND_ACCESS_LEVELS, 
  ROLE_VISIBILITY_ACCESS, 
  ROLE_PREVIEW_VISIBILITY_ACCESS,
  ENTRY_ACCESS_CONTROL, 
  ENTRIES, 
  FIXED_ROUTES,
  ROUTE_ACCESS_CONTROL,
} from "#lib/app_config.js";
import * as date_utils from '#lib/date_utils.js';
import slugify from "slugify";
import { LANG } from "#lib/i18n.svelte.js";

const TIME_ZONE = env.PUBLIC_TIME_ZONE || 'Europe/Berlin';
const TIME_LOCALE = env.PUBLIC_TIME_LOCALE;
const TIME_START_OF_WEEK = env.PUBLIC_TIME_START_OF_WEEK ? parseInt(env.PUBLIC_TIME_START_OF_WEEK, 10) : 1;
const TIME_HOUR_FORMAT = env.PUBLIC_TIME_HOUR_FORMAT;

export const MAX_IMAGE_DIMENSION = Number.parseInt(env.PUBLIC_MAX_IMAGE_DIMENSION, 10) || 4000;
export const IMAGE_QUALITY = Number.parseFloat(env.PUBLIC_IMAGE_QUALITY) || 0.92;
export const MAX_IMAGE_FILE_SIZE = parseInt(env.PUBLIC_MAX_IMAGE_FILE_SIZE) || 10000000; // ~ 10MB
// Longest side of images stored from non-admin uploads (the server scales them
// down to this, so the client need not send more). Avatars render at 100px at
// most; poster images fill a card.
export const AVATAR_MAX_DIMENSION = 1024;
export const POSTER_IMAGE_MAX_DIMENSION = 2400;
export const MAX_VIDEO_FILE_SIZE = parseInt(env.PUBLIC_MAX_VIDEO_FILE_SIZE) || 30000000; // ~ 30MB
export const MAX_VIDEO_DIMENSION = parseInt(env.PUBLIC_MAX_VIDEO_DIMENSION) || 1920; // 1920px
export const MAX_VIDEO_DURATION = parseInt(env.PUBLIC_MAX_VIDEO_DURATION) || 180; // 180 seconds (3 minutes)

// Video files should be uploaded as they are, a long as they aren't larger than MAX_VIDEO_FILE_SIZE
export const ALLOWED_VIDEO_TYPES = ['video/mp4', 'video/webm'];
// These types are considered web-optimized, and we don't resize them, use with care!
export const ALLOWED_PRE_PROCESSED_IMAGE_TYPES = ['image/webp', 'image/gif', 'image/avif', 'image/apng'];
// High-quality source image formats, we want to try to resize/optimize them
export const ALLOWED_PROCESSABLE_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/heif', 'image/heic'];
// SVG's gets special handling (and is kept as original)
export const SVG_TYPE = 'image/svg+xml';

export const ACCEPTED_IMAGE_FILE_TYPES = [
  ...ALLOWED_VIDEO_TYPES,
  ...ALLOWED_PRE_PROCESSED_IMAGE_TYPES,
  ...ALLOWED_PROCESSABLE_IMAGE_TYPES,
  SVG_TYPE
];

// What a non-admin may upload: raster images only, which the server re-encodes
// (see reencode_untrusted_image).
export const ACCEPTED_REENCODABLE_IMAGE_TYPES = [
  ...ALLOWED_PRE_PROCESSED_IMAGE_TYPES,
  ...ALLOWED_PROCESSABLE_IMAGE_TYPES,
];

const IMAGE_FILE_EXTENSIONS = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/apng': 'apng',
  'image/svg+xml': 'svg',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'image/webp': 'webp',
}

export function resolve_master_content_type(mime_type) {
  if (mime_type === 'image/jpeg') return 'image/jpeg';
  if (mime_type === 'image/png') return 'image/png';
  if (mime_type === 'image/webp') return 'image/webp';
  return 'image/webp';
}

export function get_extension_for_mime_type(mime_type) {
  return IMAGE_FILE_EXTENSIONS[mime_type] || 'webp';
}

// Pre-compile regexes for fixed routes
let _fixed_route_regex_map = null;

function get_fixed_route_regex_map() {
  if (!_fixed_route_regex_map) {
    _fixed_route_regex_map = new Map(
      FIXED_ROUTES.map(route => [
        route,
        // Matches: optional starting slash + route name + (slash OR question mark OR hash OR end of string)
        // Example matches: "calendar", "/calendar", "/calendar/", "/calendar?", "/calendar#foo"
        // Does not match: "/calendar-foo"
        new RegExp(`^(\\/)?${route}(?:\\/|\\?|#|$)`)
      ])
    );
  }
  return _fixed_route_regex_map;
}


export function unique_classes(...classes) {
  return classes.filter(Boolean).join(" ");
}

export function is_safari() {
  // Detect Chrome
  let chrome_agent = navigator.userAgent.indexOf("Chrome") > -1;
  // Detect Safari
  let safari_agent = navigator.userAgent.indexOf("Safari") > -1;
  // Discard Safari since it also matches Chrome
  if (chrome_agent && safari_agent) safari_agent = false;
  return safari_agent;
}

// We don't use "_" and "-" for better readability
const _nanoid = customAlphabet(
  "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz",
  21
);

export function nanoid() {
  return _nanoid();
}

/**
 * Generates an ID that is safe for image filenames.
 * Ensures the substring "ad" does not appear (case-insensitive),
 * since some (adblock) systems may block such URLs.
 * Falls back to regenerating until the constraint is met.
 * @returns {string}
 */
export function nanoid_image_safe() {
  let id = _nanoid();
  // Avoid any occurrence of "ad" in any casing
  while (/ad/i.test(id)) {
    id = _nanoid();
  }
  return id;
}


export function unique(array) {
  return array.filter((value, index, array) => {
    return array.indexOf(value) === index;
  });
}

// Computes the intersection of values in two given arrays
export function intersection(array1, array2) {
  return array1.filter(value => array2.includes(value));
}

export async function fetch_json(method, url, data = undefined) {
  const response = await fetch(url, {
    method: method,
    body: JSON.stringify(data),
    headers: {
      "content-type": "application/json"
    }
  });
  if (!response.ok) {
    const err = new Error(response.statusText);
    err.status = response.status;
    throw err;
  }

  // Handle empty responses (204 No Content or empty body)
  const content_length = response.headers.get('content-length');
  if (response.status === 204 || content_length === '0') {
    return null;
  }
  
  // Try to parse JSON, return null if body is empty
  const text = await response.text();
  if (!text || text.trim() === '') {
    return null;
  }
  
  return JSON.parse(text);
}

/**
 * Generates a URL based on the provided entry_id and slug.
 * @param {object} entry_or_entry_preview - A full entry record or an entry preview
 * @returns {string|undefined} - The constructed URL or undefined if no valid parameters are provided.
 */
export function entry_url(entry_or_entry_preview) {
  const {entry_id, slug} = entry_or_entry_preview;
  return slug ? `/${slug}-${entry_id}` : entry_id ? `/${entry_id}` : undefined;
}

// Iterate through all blocks of a container and look for embeds with entry_type === "image"
export function extract_images_from_container(container) {
  let images = [];
  if (!container) return images;

  let blocks = Array.isArray(container) ? container : container.blocks;

  blocks.forEach(block => {
    if (block.type === "story" && block.entry_previews?.length > 0) {
      block.entry_previews.forEach(entry_preview => {
        if (entry_preview.images[0]) {
          // HACK: We need the object to be structured like a native image entry, not like an image preview
          images.push({
            ...entry_preview,
            ...entry_preview.images[0],
          });
        }
      });
    }
  });

  // Return maximum of 5 images
  return images.slice(0, 5);
}

// Iterate through all blocks of a container and look for the first text block (for now)
// TODO: make the text block qualify better. E.g. "has between 50 and 255 characters"
export function extract_text_teaser_from_container(container) {
  if (!container) return undefined;
  
  let blocks = Array.isArray(container) ? container : container.blocks;
  let first_text_block = blocks.find(block => { return block.type === "story" && block.show_description; });
  return first_text_block?.description;
}

// Search for the first header element
// TODO: Use other text elements that are short enough to qualilify as a title
export function extract_title_from_container(container) {
  if (!container) return undefined;
  let blocks = Array.isArray(container) ? container : container.blocks;
  let first_header_block = blocks.find(block => block.type === "story" && block.show_title && block.title);
  return first_header_block?.title;
}

export function extract_preamble_from_container(container) {
  if (!container) return undefined;
  let blocks = Array.isArray(container) ? container : container.blocks;
  let first_preamble_block = blocks.find(block => block.type === "story" && block.show_preamble && block.preamble);
  return first_preamble_block?.preamble;
}

export async function fetch_entry(entry_id, { as_preview } = { as_preview: false }) {
  return fetch_json("POST", "/api/entry", { entry_id, as_preview });
}

// Same as fetch_entry but for non-unique props
export async function fetch_entries(entry_ids, { as_preview } = { as_preview: false }) {
  if (entry_ids.length === 0) return [];
  return fetch_json("POST", "/api/entries", { entry_ids, as_preview });
}

export async function resolve_link(link_href) {
  return fetch_json("POST", "/api/resolve-link", { link_href });
}


/**
 * Searches for entries based on a search term, entry type, and excluded IDs.
 * Handles parsing the search term to potentially extract an entry type.
 *
 * @param {string} search_term - The raw search term input by the user.
 * @param {string} default_entry_type - The entry type to use if not extracted from the search term.
 * @param {Array<number|string>} excluded_entry_ids - An array of entry IDs to exclude from the search.
 * @returns {Promise<Array<object>>} - A promise that resolves to an array of search result objects.
 */
export async function search_entries(search_term, default_entry_type, excluded_entry_ids, options = {}) {
  const { entry_previews } = await search_entries_paged(search_term, default_entry_type, excluded_entry_ids, options);
  return entry_previews;
}

/**
 * Like search_entries, but returns the full paged response
 * ({ entry_previews, total_count, entry_type_counts }) so callers can paginate server-side.
 */
export async function search_entries_paged(search_term = '', default_entry_type = '', excluded_entry_ids = [], {
  limit, offset, sort_by, sort_direction, visibility, exclude_types, with_counts, allow_type_sniff = true
} = {}) {
  let entry_type = default_entry_type;
  let q = search_term;

  // A type may be sniffed out of the query ("homepage" → type page, q "home") — but never
  // when the caller pinned a type: an image-only picker must stay an image-only picker.
  if (allow_type_sniff && !default_entry_type) {
    if (Object.keys(ENTRIES).includes(search_term.toLowerCase())) {
      entry_type = search_term.toLowerCase();
      q = ''; // Exact match for an entry type is a pure type filter
    } else {
      const found_entry_type = Object.keys(ENTRIES).find(type =>
        search_term.toLowerCase().includes(type.toLowerCase())
      );
      if (found_entry_type) {
        entry_type = found_entry_type.toLowerCase();
        q = search_term.toLowerCase().replace(found_entry_type.toLowerCase(), '').trim();
      }
    }
  }

  const params = new URLSearchParams({
    exclude: excluded_entry_ids?.join(',') || '',
  });

  if (q) params.append('q', q);
  if (entry_type) params.append('entry_type', entry_type);
  if (limit != null) params.append('limit', limit);
  if (offset != null) params.append('offset', offset);
  if (sort_by) params.append('sort_by', sort_by);
  if (sort_direction) params.append('sort_direction', sort_direction);
  if (visibility) params.append('visibility', visibility);
  if (exclude_types?.length) params.append('exclude_types', exclude_types.join(','));
  if (with_counts) params.append('with_counts', '1');

  const response = await fetch(`/api/search-entries?${params.toString()}`);
  if (!response.ok) {
    throw new Error(`Entry search failed (${response.status})`);
  }
  const response_json = await response.json();
  // Support both new { entry_previews, total_count } and legacy array response
  if (Array.isArray(response_json)) {
    return { entry_previews: response_json, total_count: response_json.length };
  }
  return response_json;
}


/**========================================================================
 * 
 *                   DATE UTILS (see date_utils.js) for API usage
 * 
 *========================================================================**/


export function get_utc_offset(timezone = TIME_ZONE, date = new Date()) {
  return date_utils.get_utc_offset(timezone, date);
}

export function localize_utc_date(utc_date, timezone = TIME_ZONE) {
  return date_utils.localize_utc_date(utc_date, timezone);
}

export function unlocalize_utc_date(localized_utc_date, timezone = TIME_ZONE) {
  return date_utils.unlocalize_utc_date(localized_utc_date, timezone);
}

export function yyyy_mm_dd_from_date(date) {
  return date_utils.yyyy_mm_dd_from_date(date);
}

export function format_utc_time_as_local(utc_date, timezone = TIME_ZONE) {
  return date_utils.format_utc_time_as_local(utc_date, timezone);
}

export function convert_local_time_to_utc(local_time, reference_date = new Date(), timezone = TIME_ZONE) {
  return date_utils.convert_local_time_to_utc(local_time, reference_date, timezone);
}

export function format_utc_date_as_local(utc_date, timezone = TIME_ZONE, locale = TIME_LOCALE) {
  return date_utils.format_utc_date_as_local(utc_date, timezone, locale);
}

export function update_utc_date_preserve_time(new_date, reference_date) {
  return date_utils.update_utc_date_preserve_time(new_date, reference_date, TIME_ZONE);
}

export function format_date(date, parts = 'DMY', locale = TIME_LOCALE ?? 'en-GB', time_zone = TIME_ZONE ?? 'UTC') {
  return date_utils.format_date(date, parts, locale, time_zone, TIME_HOUR_FORMAT);
}

export function create_utc_timestamp_that_appears_as_local_midnight(date_value, timezone = TIME_ZONE) {
  return date_utils.create_utc_timestamp_that_appears_as_local_midnight(date_value, timezone);
}

export function today_as_local(timezone = TIME_ZONE) {
  return date_utils.today_as_local(timezone);
}


/**========================================================================
 * 
 * Sorts an array of events by a specified key without mutating the original array.
 *
 * @param {Array<Object>} events - Array of event objects to be sorted.
 * @param {string} key - The key in the event objects to sort by.
 * @param {'asc'|'desc'} [sort='asc'] - Sort order
 * 
 *========================================================================**/
export function sort_events_by_key(events, key, sort = 'asc') {
  return [...events].sort((a, b) => {
    const diff = new Date(a[key]) - new Date(b[key]);
    return sort === 'asc' ? diff : -diff;
  });
}

export function get_week_number(date_string) {
  return date_utils.get_week_number(date_string);
}



/**========================================================================
 * 
 *                        GET WEEK NUMBERS FOR MONTH AND YEAR
 * 
 * Calculates the ISO week numbers for all weeks that could appear in a 42-day month view,
 * potentially spanning into the previous and next months.
 * 
 * @param {number} month_index - The zero-based index of the month (0 for January, 11 for December).
 * @param {number} year - The year.
 * @returns {Array<number>} - An array of ISO week numbers for the month view.
 * 
 *========================================================================**/
export function get_week_numbers_for_month_and_year(month_index, year) {
  const weeks = [];
  let first_day_of_month = new Date(Date.UTC(year, month_index, 1));
  // Calculate the start of the first week in the month view
  let start_of_week_date = start_of_week(first_day_of_month);
  // Generate dates until we have 6 weeks, each week starting 7 days apart
  for (let i = 0; i < 6; i++) {
    weeks.push(get_week_number(start_of_week_date.toISOString()));
    start_of_week_date.setDate(start_of_week_date.getDate() + 7); // Move to the start of the next week
  }
  return [...new Set(weeks)];
}


/**
 * Generates an array of 42 consecutive days for a specified month and year,
 * starting at the business local start of the week for that month.
 *
 * It computes the "first day" using create_utc_timestamp_that_appears_as_local_midnight so that the days are based on
 * the business's local midnight.
 *
 * @param {number} month_index - The zero-based index of the month (0 for January, 11 for December).
 * @param {number} year - The year for which the month sheet is being generated.
 * @param {Object} [options] - Configuration options
 * @param {'date'|'iso'} [options.format='date'] - Output format ('date' for Date objects, 'iso' for YYYY-MM-DD strings)
 * @param {string} [options.timezone=TIME_ZONE] - IANA timezone string for business local time.
 * @returns {Array<Date|string>} - An array of Date objects or date strings representing each day on the month sheet
 */
export function generate_calendar_month_sheet_business_local(month_index, year, { format = 'date', timezone = TIME_ZONE } = {}) {

  // The weekday of a calendar date is timezone-independent, so we can work in
  // pure calendar space (plain UTC date math) to determine where the sheet starts.
  const first_of_month = new Date(Date.UTC(year, month_index, 1));
  const day_of_week = first_of_month.getUTCDay(); // 0: Sunday, 1: Monday, etc.

  // Calculate the offset (in days) to start at the beginning of the week
  // TIME_START_OF_WEEK is typically set to 1 (Monday)
  const offset = (day_of_week - TIME_START_OF_WEEK + 7) % 7;

  const days = [];
  for (let i = 0; i < 42; i++) {
    // Step whole calendar days via Date.UTC normalization instead of fixed
    // 24h-in-ms increments: local days on DST transitions are 23h/25h long,
    // so ms arithmetic emits a date twice (fall-back) or skips one (spring-forward).
    const current_day = new Date(Date.UTC(year, month_index, 1 - offset + i));
    if (format === 'iso') {
      days.push(yyyy_mm_dd_from_date(current_day));
    } else {
      // Return UTC timestamps that appear as business-local midnight of each day.
      days.push(create_utc_timestamp_that_appears_as_local_midnight(yyyy_mm_dd_from_date(current_day), timezone));
    }
  }
  return days;
}


export function start_of_year(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 0, 0, 0));
}

export function end_of_year(date) {
  // We need to fill up the hours till the end of that last day of the month
  return new Date(Date.UTC(date.getUTCFullYear(), 11, 31, 23, 59, 59, 999));
}

export function get_start_of_month(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1, 0, 0, 0));
}

export function get_end_of_month(date) {
  return new Date(Date.UTC(
    date.getUTCFullYear(), 
    date.getUTCMonth() + 1, 
    0,  // Last day of previous month
    23, 59, 59, 999
  ));
}

export function get_start_of_year(date) {
  return new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 0, 0, 0));
}

export function is_same_day(date_left, date_right) {
  return date_left.getUTCFullYear() === date_right.getUTCFullYear() &&
         date_left.getUTCMonth() === date_right.getUTCMonth() &&
         date_left.getUTCDate() === date_right.getUTCDate();
}


/**========================================================================
 * 
 * Determines the start or end of the week for a given date and locale.
 * @param {Date} date - The date for which to find the start or end of the week.
 * @param {string} locale - The locale that determines the first day of the week.
 * @param {'start' | 'end'} mode - Specifies whether to find the start or end of the week.
 * @returns {Date} The start or end of the week based on the mode.
 * 
 *========================================================================**/
export function calculate_week_bound(date, locale, mode) {
  // Use the UTC day to ensure arithmetic is done in UTC.
  const day_of_week = date.getUTCDay();
  let adjustment;
  if (TIME_START_OF_WEEK === 0) {
    adjustment = mode === 'start' ? -day_of_week : 6 - day_of_week;
  } else if (TIME_START_OF_WEEK === 6) {
    adjustment = mode === 'start' ? -((day_of_week + 1) % 7) : (13 - day_of_week) % 7;
  } else {
    adjustment = mode === 'start' ? -((day_of_week + 6) % 7) : (7 - day_of_week) % 7;
  }

  // Clone the date using its numeric value and adjust using UTC methods.
  const new_date = new Date(date.getTime());
  new_date.setUTCDate(date.getUTCDate() + adjustment);
  
  if (mode === 'start') {
    new_date.setUTCHours(0, 0, 0, 0);
  } else {
    new_date.setUTCHours(23, 59, 59, 999);
  }
  return new_date;
}

/**========================================================================
 * 
 * Gets the start of the week for a given date, considering the locale.
 * @param {Date} date - The date for which to find the start of the week.
 * @param {string} locale - The locale to determine the first day of the week.
 * @returns {Date} A new Date object representing the start of the week.
 * 
 *========================================================================**/
export function start_of_week(date, locale = TIME_LOCALE) {
  return calculate_week_bound(date, locale, 'start');
}

/**========================================================================
 * 
 * Gets the end of the week for a given date, considering the locale.
 * @param {Date} date - The date for which to find the end of the week.
 * @param {string} locale - The locale to determine the last day of the week.
 * @returns {Date} A new Date object representing the end of the week.
 * 
 *========================================================================**/
export function end_of_week(date, locale = TIME_LOCALE) {
  return calculate_week_bound(date, locale, 'end');
}

export function get_start_of_day(date) {
  return new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    0, 0, 0
  ));
}

export function get_end_of_day(date) {
  return new Date(Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    23, 59, 59, 999
  ));
}

/**========================================================================
 * 
 * Checks if a given date is within a specified date range, including the entire end date.
 *
 * @param {Date} target_date - The date to check.
 * @param {Date} start_date - The start date of the range, beginning from the start of the day.
 * @param {Date} end_date - The end date of the range, extended to the end of the day.
 * @returns {boolean} - True if target_date is within the range, false otherwise.
 * 
 *========================================================================**/
export function is_date_within_days(target_date, start_date, end_date) {
  const _target_date = new Date(Date.UTC(
    target_date.getUTCFullYear(),
    target_date.getUTCMonth(),
    target_date.getUTCDate(),
    0, 0, 0
  ));
  const _start_date = new Date(Date.UTC(
    start_date.getUTCFullYear(),
    start_date.getUTCMonth(),
    start_date.getUTCDate(),
    0, 0, 0
  ));
  const _end_date = new Date(Date.UTC(
    end_date.getUTCFullYear(),
    end_date.getUTCMonth(),
    end_date.getUTCDate(),
    23, 59, 59, 999
  ));

  return _target_date >= _start_date && _target_date <= _end_date;
}


/**========================================================================
 *
 *                   GENERATE DATE RANGE
 *
 * Generates an array of dates between and including the start and end dates.
 * Each element in the returned array is a new Date instance or formatted string
 * representing a day in the range from the start_date to the end_date, inclusive.
 *
 * @param {Date} start_date - The beginning of the date range.
 * @param {Date} end_date - The end of the date range.
 * @param {Object} [options] - Configuration options
 * @param {'date'|'iso'} [options.format='date'] - Output format ('date' for Date objects, 'iso' for YYYY-MM-DD strings)
 * @returns {Array<Date|string>} - An array of Date objects or date strings for each day in the range
 * 
 *========================================================================**/
export function create_array_of_dates_for_range(start_date, end_date, { format = 'date' } = {}) {
  if (!validate_date(start_date) || !validate_date(end_date)) {
    throw new Error('Invalid parameters: "start_date" and "end_date" must be valid JavaScript Date objects');
  }

  const dates = [];
  let current_date = new Date(Date.UTC(
    start_date.getUTCFullYear(),
    start_date.getUTCMonth(),
    start_date.getUTCDate()
  ));
  const utc_end_date = new Date(Date.UTC(
    end_date.getUTCFullYear(),
    end_date.getUTCMonth(),
    end_date.getUTCDate()
  ));
  
  while (current_date <= utc_end_date) {
    // Create a new date object or format as ISO string based on options
    dates.push(format === 'iso' ? 
      yyyy_mm_dd_from_date(current_date) : 
      new Date(current_date)
    );
    current_date.setUTCDate(current_date.getUTCDate() + 1);
  }
  return dates;
}


/**========================================================================
 * 
 *                          FORMAT DATE RANGE
 * 
 * Formats a date range based on various criteria:
 * - Whether the event is all-day (affects time display).
 * - If start and end dates are on the same day (affects end date display).
 * - If dates are in the same year, omit the year for the start date.
 * - If dates are in the same month and year, omit the month and year for the start date.
 * Includes an optional separator between start and end dates.
 *
 * @param {Date} starts_at - The start date of the event.
 * @param {Date} ends_at - The end date of the event.
 * @param {boolean} all_day - Flag indicating if the event is an all-day event.
 * @param {string} [separator=' – '] - The separator string to use between start and end dates.
 * @param {boolean} [short=false] - Whether to use a short format.
 * @returns {string} - The formatted date range string.
 * 
 *========================================================================**/
export function format_date_range(starts_at, ends_at, all_day, separator = ' – ', short = false) {
  const start_date_utc = new Date(starts_at);
  const start_date_valid = validate_date(start_date_utc);
  const end_date_utc = new Date(ends_at);
  const end_date_valid = validate_date(end_date_utc);
  if (!start_date_valid) {
    return '';
  }

  const safe_end_date_utc = end_date_valid ? end_date_utc : start_date_utc;

  // Compare using business-local dates to avoid UTC midnight drift
  const start_date_local = localize_utc_date(start_date_utc);
  const end_date_local = localize_utc_date(safe_end_date_utc);
  const same_day = is_same_day(start_date_local, end_date_local);
  
  const month_format = short ? 'MMM' : 'MMMM';

  // Handle all-day events
  if (all_day) {
    if (same_day) {
      // Single day all-day event: "Monday, January 1 2024"
      return format_date(safe_end_date_utc, `${month_format}DEEEY`);
    }
    // Multi-day all-day event: "Monday, January 1 – Tuesday, January 2 2024"
    return `${format_date(start_date_utc, `${month_format}DEEE`)}${separator}${format_date(safe_end_date_utc, `${month_format}DEEEY`)}`;
  }

  // Handle timed events
  if (same_day) {
    const same_time = start_date_local.getUTCHours() === end_date_local.getUTCHours() && 
                     start_date_local.getUTCMinutes() === end_date_local.getUTCMinutes();
    
    if (same_time) {
      // Single time event: "Monday, January 1 2024 14:30"
      return format_date(safe_end_date_utc, `${month_format}DEEEYHm`);
    }
    // Same day, different times: "Monday, January 1 2024 14:30 – 15:45"
    return `${format_date(start_date_utc, `${month_format}DEEEY Hm`)}${separator}${format_date(safe_end_date_utc, 'Hm')}`;
  }

  // Different days with times: "January 1 14:30 – January 2 15:45 2024"
  return `${format_date(start_date_utc, `${month_format}DYHm`)}${separator}${format_date(safe_end_date_utc, `${month_format}DYHm`)}`;
}


/**========================================================================
 *
 * Adds a specified number of days to a given date.
 *
 * @param {Date} date - The date to which days will be added.
 * @param {number} days - The number of days to add to the date.
 * @returns {Date} - A new Date object representing the date after adding the specified days.
 * 
 *========================================================================**/
export function add_days(date, days) {
  const result = new Date(date);
  result.setUTCDate(result.getUTCDate() + days);
  return result;
}

// See date_utils.js for API docs
export function validate_date(date) {
  return date_utils.validate_date(date);
}

/**========================================================================
 * 
 * Organises events by date within a dynamically calculated period based on 'from' and 'to' dates.
 * Generates day keys in the "YYYY-MM-DD" format.
 *
 * @param {Object[]} events - Array of event objects.
 * @param {Date} from - Start date.
 * @param {Date} to - End date.
 * @returns {Object} Events organised by date with keys in the format "YYYY-MM-DD".
 * 
 *========================================================================**/

export function organise_events_by_day(events, from, to) {
  if (!validate_date(from) || !validate_date(to)) {
    console.error("invalid from:", from);
    console.error("invalid to:", to);
    throw new Error('Invalid parameters: "from" and "to" must be valid JavaScript Date objects');
  }

  const result = {};
  const start_date = new Date(from);
  const end_date = new Date(to);

  // Iterate day-by-day of the whole year
  for (let day = new Date(start_date); day <= end_date; day.setUTCDate(day.getUTCDate() + 1)) {
    const day_key = yyyy_mm_dd_from_date(day);
    result[day_key] = [];
  }
  
  // Iterate through events and add them to the corresponding day(s)
  events.forEach(event => {
    // Convert event times to local business time for comparison
    const event_start = localize_utc_date(new Date(event.starts_at));
    const event_end = localize_utc_date(new Date(event.ends_at));
    const dates_of_event = create_array_of_dates_for_range(event_start, event_end, { format: 'iso' });

    for (const date of dates_of_event) {
      // We nee to make this robst as during the transition from view (e.g. monthly -> weekly) 
      // organise_events_by_day gets called with the new from, to but the old events are (until it has finished loading)
      result[date]?.push(event);
    }
  });

  return result;
}


/**========================================================================
 *

 *                     adjust column count to fit panel
 *
 *
 *========================================================================**/

/**
 * Calculates the maximum height of the tallest column based on a specified number of columns.
 * @param {Array} aspect_ratios - The sorted aspect ratios.
 * @param {number} max_panel_size - The maximum size of the panel.
 * @param {number} column_count - The number of columns to distribute the aspect ratios across.
 * @returns {number} The height of the tallest column.
 */
export function calculate_max_column_height(
  aspect_ratios,
  max_panel_size,
  column_count
) {
  const columnWidth = max_panel_size / column_count;
  let heights = new Array(column_count).fill(0);
  let currentColumn = 0;

  aspect_ratios.forEach(({ ratio }) => {
    heights[currentColumn] += columnWidth / ratio;
    currentColumn = (currentColumn + 1) % column_count;
  });

  return Math.max(...heights);
}

/**
 * Polyfill for "Grid Masonry"
 * Adjusts the column count to ensure the height of the tallest column is within the maximum panel size.
 * @param {Array} aspect_ratios - The sorted aspect ratios.
 * @param {number} max_panel_size - The maximum size of the panel.
 * @param {number} initialColumn_count - The initial column count.
 * @returns {number} The adjusted column count.
 */
export function adjust_column_count_to_fit_panel(
  aspect_ratios,
  max_panel_size,
  initialColumn_count
) {
  let column_count = initialColumn_count;
  let totalHeight = calculate_max_column_height(
    aspect_ratios,
    max_panel_size,
    column_count
  );

  while (totalHeight > max_panel_size) {
    column_count++;
    totalHeight = calculate_max_column_height(
      aspect_ratios,
      max_panel_size,
      column_count
    );
  }

  return column_count;
}

/**========================================================================
 *
 *
 *                           RESIZE IMAGE
 *
 *
 *========================================================================**/


/**
 * Resizes an image file to fit within max_dimension.
 *
 * @param {File} file - The image file to resize.
 * 
 * @param {Object} options - Resize options
 * @param {number} [options.quality] - The quality of the resized image, between 0 and 1.
 * @param {number} [options.max_dimension] - the maximum length for the longest side (width/height) of the image in pixels 
 * @param {string} [options.content_type] - The MIME type of the resized image (e.g., "image/webp").
 * 
 * @returns {Promise<{blob: Blob, width: number, height: number}>} A promise that resolves with the resized image as a Blob and the final dimensions
 */
export async function resize_image(
  file,
  {
    quality = IMAGE_QUALITY,
    max_dimension = MAX_IMAGE_DIMENSION,
    content_type = "image/webp"
  }
) {
  const image_source = await read_image_source(file);
  const { width: source_width, height: source_height } = image_source;

  let width = source_width;
  let height = source_height;

  // Downsize if resized dimensions exceed max_dimension
  if (Math.max(width, height) > max_dimension) {
    const ratio = max_dimension / Math.max(width, height);
    width = width * ratio;
    height = height * ratio;
  }

  const target_width = Math.round(width);
  const target_height = Math.round(height);
  const canvas = create_resize_canvas(target_width, target_height);
  const context = canvas.getContext("2d");
  if (!context) {
    image_source.release?.();
    throw new Error("Failed to get 2D context");
  }

  context.drawImage(image_source.source, 0, 0, target_width, target_height);
  const blob = await canvas_to_blob(canvas, content_type, quality);
  image_source.release?.();

  return {
    width: target_width,
    height: target_height,
    blob,
  };
}

async function read_image_source(file) {
  // Prefer createImageBitmap for better performance when available
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file);
      return {
        source: bitmap,
        width: bitmap.width,
        height: bitmap.height,
        release: () => bitmap.close?.()
      };
    } catch {
      // Fall back to Image element (e.g. HEIC on Safari)
    }
  }

  const data_url = await file_to_data_url(file);
  const image = await load_image(data_url);
  return {
    source: image,
    width: image.width,
    height: image.height,
    release: () => {}
  };
}

function file_to_data_url(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
}

function load_image(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = (error) => reject(error);
    image.src = src;
  });
}

function create_resize_canvas(width, height) {
  if (typeof OffscreenCanvas !== 'undefined') {
    return new OffscreenCanvas(width, height);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  return canvas;
}

async function canvas_to_blob(canvas, content_type, quality) {
  if (typeof canvas.convertToBlob === 'function') {
    return canvas.convertToBlob({ type: content_type, quality });
  }
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to create blob'));
        return;
      }
      resolve(blob);
    }, content_type, quality);
  });
}

/**
 * Retrieves video metadata such as dimensions and duration.
 * @param {File} file - The video file from which metadata is extracted.
 * @returns {Promise<{width: number, height: number, duration: number, audio_track: boolean}>} A promise that resolves with video metadata.
 */
export async function get_video_meta_data(file) {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    
    video.onloadedmetadata = function () {
      try {
        // Use captureStream() to detect audio tracks
        const stream = video.captureStream();
        const audio_track = stream.getAudioTracks().length > 0;
        
        resolve({
          width: this.videoWidth,
          height: this.videoHeight,
          duration: this.duration,
          audio_track
        });
      } catch (error) {
        // Fallback method if captureStream fails
        const audio_context = new (window.AudioContext || window.webkitAudioContext)();
        const media_source = audio_context.createMediaElementSource(video);
        const audio_track = media_source.channelCount > 0;
        audio_context.close();
        
        resolve({
          width: this.videoWidth,
          height: this.videoHeight,
          duration: this.duration,
          audio_track
        });
      } finally {
        URL.revokeObjectURL(video.src);
      }
    };
    
    video.onerror = function () {
      reject(video.error);
      URL.revokeObjectURL(video.src); // Revoke URL on error as well to avoid memory leaks
    };
    
    video.src = URL.createObjectURL(file);
  });
}


export async function get_svg_metadata(file) {
  function __get_metadata(svg_string) {
    let doc = (new DOMParser()).parseFromString(svg_string, 'text/html');
    let width = doc.querySelector('svg').getAttribute('width');
    let height = doc.querySelector('svg').getAttribute('height');
    const view_box = doc.querySelector('svg').getAttribute('viewBox');
    const alt = doc.querySelector('svg').getAttribute('alt') ?? '';
  
    // Parse viewBox for width and height if they are not explicitly set
    if (!width || !height) {
      if (view_box) {
        const view_box_values = view_box.split(' ').map(v => parseFloat(v));
        if (view_box_values.length === 4) {
          width = width ?? view_box_values[2].toString();
          height = height ?? view_box_values[3].toString();
        } else {
          const message = 'Invalid SVG File. `viewBox` must have 4 values.';
          alert(message);
          throw new Error(message);
        }
      } else {
        const message = 'Invalid SVG File. `width` and `height` must be provided on the `<svg>` element or in the `viewBox`.';
        alert(message);
        throw new Error(message);
      }
    }
  
    return { width, height, alt };
  }

  return new Promise((resolve, reject) => {
    let fr = new FileReader();
    fr.onload = function() {
      const { width, height, alt } = __get_metadata(fr.result);
      resolve({ data: fr.result, width, height, alt});
    }
    fr.onerror = function () {
      reject(fr.error);
    };

    fr.readAsText(file);
  })
}

export async function get_image_dimensions(file) {
  return new Promise((resolve, reject) => {
    const img = document.createElement('img');
    img.onload = function () {
      resolve({ width: this.width, height: this.height });
    };
    img.onerror = function () {
      reject(img.error);
    };
    img.src = URL.createObjectURL(file);
  });
}


function determine_media_type(file_type) {
  return ALLOWED_VIDEO_TYPES.includes(file_type) ? 
    'video' : 
    [...ALLOWED_PROCESSABLE_IMAGE_TYPES, ...ALLOWED_PRE_PROCESSED_IMAGE_TYPES, SVG_TYPE].includes(file_type) ? 'image' : 
    'unknown';
}

/**
 * Validates a media file against various criteria.
 * @param {File} file - The file to validate.
 * @param {object} [metadata] - Optional metadata, e.g., for videos { width: number, height: number, duration?: number }.
 * @returns {{success: true} | {success: false, errors: Array<string>}} 
 *          An object with `{ success: true }` if valid, or `{ success: false, errors: Array<string> }` if invalid.
 */
function validate_media(file, metadata = null) {
  const errors = [];
  const media_type = determine_media_type(file.type);

  if (media_type === 'video') {
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      errors.push('invalid_video_format');
    }
    if (metadata && (metadata.width > MAX_VIDEO_DIMENSION || metadata.height > MAX_VIDEO_DIMENSION)) {
      errors.push('video_resolution_too_high');
    }
    if (metadata && typeof metadata.duration === 'number' && metadata.duration > MAX_VIDEO_DURATION) {
      errors.push('video_duration_too_long');
    }
    if (file.size > MAX_VIDEO_FILE_SIZE) {
      errors.push('video_file_too_large');
    }
  } else if (media_type === 'image') {
    if (!ALLOWED_PROCESSABLE_IMAGE_TYPES.includes(file.type) && file.size > MAX_IMAGE_FILE_SIZE) {
      errors.push('image_file_too_large');
    }
    if (['image/heic', 'image/heif'].includes(file.type) && !is_safari()) {
      errors.push('unsupported_image_format_on_browser');
    }
  } else if (media_type === 'unknown') {
    // If determine_media_type explicitly says 'unknown', it's an unsupported file type.
    errors.push('unsupported_file_type');
  }

  if (errors.length > 0) {
    return { success: false, errors };
  }
  return { success: true };
}


/**
 * Upload a preprocessed media file under a fresh generated asset path.
 * Throws on failure — callers surface the error inline (no alert(), no
 * swallowed rejection: a failed upload must never look successful).
 *
 * @returns {Promise<{path: string, analysis?: object|null, deduped?: boolean, existing_entry_id?: number}>}
 *   The server's upload response: the stored (or deduped) asset path, plus the
 *   upload-time image analysis (auto focal point, graphic detection) when available.
 */
export async function upload_with_progress(optimized_file, metadata, progress_callback) {
  const file_extension = IMAGE_FILE_EXTENSIONS[metadata.mime_type];
  let path = [["images", nanoid_image_safe()].join("/"), file_extension].join(".");
  const response = await upload_asset_json(optimized_file, path, progress_callback);
  return response?.path ? response : { path };
}

/**
 * Start polling image variant status for one asset.
 * Returns a stop handler that clears pending timers.
 * @param {Object} options
 * @param {string} options.asset_id
 * @param {number=} options.interval_ms
 * @param {number=} options.max_attempts
 * @param {(status: {pending: number, variants: Array}) => void=} options.on_status
 * @param {(result: { reason: 'done' | 'timeout' }) => void=} options.on_complete
 * @param {(error: unknown) => void=} options.on_error
 * @returns {{stop: () => void}}
 */
export function create_image_variant_status_poller({
  asset_id,
  interval_ms = 3000,
  max_attempts = 140,
  on_status,
  on_complete,
  on_error
}) {
  let poll_timer;
  let attempt_count = 0;
  let is_stopped = false;

  const stop = () => {
    is_stopped = true;
    if (poll_timer) {
      clearTimeout(poll_timer);
      poll_timer = undefined;
    }
  };

  const schedule_next_poll = () => {
    if (is_stopped) return;
    poll_timer = setTimeout(run_poll, interval_ms);
  };

  async function run_poll() {
    if (is_stopped || !asset_id) return;
    attempt_count += 1;

    try {
      const response = await fetch(`/api/image-variant-status?asset_id=${encodeURIComponent(asset_id)}`);
      if (!response.ok) {
        throw new Error(`Image variant status request failed (${response.status})`);
      }

      const status = await response.json();
      on_status?.(status);

      if ((status?.pending ?? 0) <= 0) {
        stop();
        await on_complete?.({ reason: 'done' });
        return;
      }
    } catch (error) {
      on_error?.(error);
    }

    if (attempt_count >= max_attempts) {
      stop();
      await on_complete?.({ reason: 'timeout' });
      return;
    }

    schedule_next_poll();
  }

  schedule_next_poll();
  return { stop };
}

async function resize_processable_image(file, max_dimension) {
  const content_type = resolve_master_content_type(file.type);
  const resized = await resize_image(file, {
    quality: IMAGE_QUALITY,
    content_type,
    max_dimension,
  });
  return { ...resized, content_type };
}

/**
 * Preprocesses a media file: validates, and for images, resizes/optimizes.
 * Videos are validated for format, dimensions, duration, and size but not processed further.
 * @param {File} file - The file to preprocess.
 * @param {{max_dimension?: number}} [options] - longest side of a resized image;
 *   smaller than the default for uploads the server scales down anyway
 * @returns {Promise<{optimized_file: File, metadata: object}|{errors: Array<{code: string}>}|undefined>}
 *          An object with optimized_file and metadata if successful and processing is needed.
 *          If validation fails, returns an object `{ errors: Array<{code: string}> }` containing the validation errors.
 *          Returns undefined if a truly unexpected issue occurs during processing (e.g., failure in get_video_meta_data).
 *          For videos that pass validation, optimized_file is the original file.
 */
export async function preprocess_media(file, { max_dimension = MAX_IMAGE_DIMENSION } = {}) {
  let media_type = determine_media_type(file.type);
  let optimized_file,
    media_width,
    media_height,
    media_duration,
    audio_track,
    media_alt,
    mime_type;

  if (media_type === 'video') {
    let video_meta;
    try {
      video_meta = await get_video_meta_data(file);
    } catch (e) {
      console.error("Error getting video metadata:", e);
      return { errors: [{ code: 'video_metadata_error' }] }; // Specific error for metadata failure
    }
    
    const validation_result = validate_media(file, video_meta);
    if (!validation_result.success) {
      return validation_result
    }

    media_width = video_meta.width;
    media_height = video_meta.height;
    media_duration = video_meta.duration;
    audio_track = video_meta.audio_track;
    optimized_file = file; // Videos are not processed further, just validated
    mime_type = file.type;
  } else if (media_type === 'image') {
    const validation_result = validate_media(file);
    if (!validation_result.success) {
      return validation_result;
    }

    if (ALLOWED_PROCESSABLE_IMAGE_TYPES.includes(file.type)) {
      const { blob, width, height, content_type } = await resize_processable_image(file, max_dimension);
      media_width = width;
      media_height = height;
      const new_extension = get_extension_for_mime_type(content_type);
      mime_type = content_type;
      optimized_file = new File([blob], `${file.name.split(".")[0]}.${new_extension}`, { type: mime_type });
    } else if (ALLOWED_PRE_PROCESSED_IMAGE_TYPES.includes(file.type)) {
      const { width, height } = await get_image_dimensions(file);
      if (Math.max(width, height) > max_dimension) {
        const content_type = resolve_master_content_type(file.type);
        const { blob, width: resized_width, height: resized_height } = await resize_image(file, {
          quality: IMAGE_QUALITY,
          content_type,
          max_dimension,
        });
        const new_extension = get_extension_for_mime_type(content_type);
        optimized_file = new File([blob], `${file.name.split(".")[0]}.${new_extension}`, { type: content_type });
        mime_type = content_type;
        media_width = resized_width;
        media_height = resized_height;
      } else {
        optimized_file = file;
        mime_type = file.type;
        media_width = width;
        media_height = height;
      }
    } else if (file.type === SVG_TYPE) {
      const { width: svg_width, height: svg_height, alt: svg_alt } = await get_svg_metadata(file);
      const svg_size = 5000;
      const aspect_ratio = svg_width / svg_height;
      if (svg_width < svg_height) {
        media_width = svg_size;
        media_height = Math.round(media_width / aspect_ratio);
      } else {
        media_height = svg_size;
        media_width = Math.round(media_height * aspect_ratio);
      }
      media_alt = svg_alt;
      optimized_file = file;
      mime_type = SVG_TYPE;
    } else {
      // This 'else' within media_type === 'image' block might be for image types 
      // that passed initial ALLOWED_X checks in validate_media but aren't one of the three above.
      // Should be rare if validate_media is comprehensive for image types.
      console.warn('Unhandled image type after validation:', file.name, file.type);
      return { errors: ['unsupported_file_type'] }; 
    }
  } else {
    // This top-level 'else' handles files not initially 'video' or 'image'.
    // Validate them to get any specific errors.
    const validation_result = validate_media(file, null);
    if (!validation_result.success) {
      return validation_result;
    }
    // If it wasn't 'video' or 'image', and validate_media still says success (e.g. passed generic checks),
    // then it's an unhandled type for processing.
    console.warn('Could not determine processing strategy for unclassified file type:', file.name, file.type);
    return { errors: ['unsupported_file_type'] };
  }

  return {
    optimized_file,
    metadata: {
      media_type,
      width: media_width,
      height: media_height,
      duration: media_duration,
      audio_track,
      alt: media_alt,
      mime_type
    }
  };
}





/**========================================================================
 *
 *
 *                           UPLOAD ASSET
 *
 *
 *========================================================================**/

function upload(file, path, progressCallback, { method = 'PUT', url = '/api/upload-asset' } = {}) {
  const formData = new FormData();
  formData.append("file", file);
  if (path) formData.append("path", path);

  return new Promise(function (resolve, reject) {
    const xhr = new XMLHttpRequest();
    // A stalled connection must fail loudly instead of hanging the upload UI forever
    xhr.timeout = 120000;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        if (xhr.status === 200) {
          resolve(xhr);
        } else {
          let message = '';
          try { message = JSON.parse(xhr.responseText)?.message || ''; } catch { /* not JSON */ }
          reject(Object.assign(new Error(message || `Upload failed (${xhr.status || 'network error'})`), { status: xhr.status }));
        }
      }
    };
    xhr.ontimeout = () => reject(new Error('Upload timed out'));

    if (progressCallback) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const percentComplete = (e.loaded / file.size) * 100;
          progressCallback(parseInt(percentComplete, 10));
        }
      };
    }
    xhr.open(method, url);
    xhr.send(formData);
  });
}

/**
 * Uploads an asset to a specified path with optional progress updates.
 *
 * @param {File} file - The asset file to be uploaded.
 * @param {string} path - The path where the asset should be uploaded.
 * @param {function(number):void} [onprogress] - Optional callback for progress updates.
 * @returns {Promise<string>} A promise that resolves with the path string on successful upload.
 */
export async function upload_asset(file, path, onprogress) {
  await upload(file, path, onprogress);
  return path;
}

/**
 * Uploads an image to one of the narrow, re-encoding endpoints open to
 * non-admins ('/api/upload-avatar', '/api/upload-poster-image'). The server
 * picks the storage path; resolves with its JSON response ({ path, … }).
 */
export async function upload_image_via(url, file, onprogress) {
  const xhr = await upload(file, null, onprogress, { method: 'POST', url });
  return JSON.parse(xhr.responseText);
}

/**
 * Translation key and params for an upload_image_via failure, for t():
 * the server refuses too large (413) and unreadable (400) images.
 * @returns {[string, Object?]}
 */
export function image_upload_error(err) {
  if (err?.status === 413) return ['image_upload_too_large', { size_limit_mb: Math.round(MAX_IMAGE_FILE_SIZE / 1e6) }];
  if (err?.status === 400) return ['image_upload_unsupported'];
  return ['upload_failed'];
}

/**
 * Like upload_asset, but returns the server's parsed JSON response
 * ({ path, analysis, deduped, existing_entry_id }) instead of just the path.
 */
export async function upload_asset_json(file, path, onprogress) {
  const xhr = await upload(file, path, onprogress);
  try {
    return JSON.parse(xhr.responseText);
  } catch {
    return { path };
  }
}

/**========================================================================
 *
 *
 *                     RESIZE DIMENSIONS TO FIT CONTAINER
 *
 *
 *========================================================================**/

/**
 * Calculates the width and height to best fit content within a container while maintaining its aspect ratio.
 *
 * @param {number} container_width - The width of the container.
 * @param {number} container_height - The height of the container.
 * @param {number} content_width - The natural width of the content (e.g., image).
 * @param {number} content_height - The natural height of the content (e.g., image).
 * @param {boolean} scale_to_fit - If true, scale content to fit within the container; otherwise, use container dimensions.
 * @returns {{width: number, height: number}} The calculated width and height.
 */
export function resize_dimensions_to_fit_container(
  container_width,
  container_height,
  content_width,
  content_height,
  scale_to_fit
) {
  let width, height;

  if (scale_to_fit) {
    let aspect_ratio = content_width / content_height;

    if (aspect_ratio > 1) {
      // Landscape or square content
      width = Math.min(container_width, container_height * aspect_ratio);
      height = width / aspect_ratio;
    } else {
      // Portrait content
      height = Math.min(container_height, container_width / aspect_ratio);
      width = height * aspect_ratio;
    }
  } else {
    width = container_width;
    height = container_height;
  }

  return { width, height };
}


/**========================================================================
 * 
 * 
 *                           FIRST LETTER UPPERCASE
 * 
 * 
 *========================================================================**/
export function capitalize_first_letter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Simple hash function to create a unique identifier for a string
 * @param {string} str - The string to hash
 * @returns {number} - Hash value
 */
export function hash_string(str) {
  if (!str) return 0;
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  return hash;
}


/**========================================================================
 * 
 * 
 *                   HANDLE KEYDOWN EVENTS IN NUMBER INPUTS
 * 
 * @param {KeyboardEvent} event - The keyboard event object
 *========================================================================**/
export function block_non_numeric_keypresses(event) {
  const allowedKeys = [
    'Backspace', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Tab', 'Delete', 'End', 'Home', 'Enter', 'Escape'
  ];
  const key = event.key;
  const isModifierKey = event.metaKey || event.altKey || event.ctrlKey || event.shiftKey;

  if (!allowedKeys.includes(key) && !key.match(/^\d$/) && !isModifierKey) {
    event.preventDefault();
  }
}


/**
 * Takes an input (string) and turns it into an number within a given range.
 * 
 * @param {string} input - The current value of the input
 * @param {number} min - The minimum allowed value
 * @param {number} max - The maximum allowed value
 * @param {number} decimals - Allowed decimals (0 for integers)
 * @param {number} default_value - Default value if input cannot be parsed
 * @returns {number} - The adjusted value within the specified min/max range
 **/
export function constrain_to_number_range(input, min, max, decimals = 0, default_value) {
  let value;
  if (decimals > 0) {
    value = parseFloat(Number(input).toFixed(decimals));
  } else {
    value = parseInt(input, 10);
  }
  if (isNaN(value)) return default_value ?? min;

  if (typeof min === 'number' && value < min) {
    value = min;
  } else if (typeof max === 'number' && value > max) {
    value = max;
  }
  
  return value;
}


/**========================================================================
 * 
 * 
 *                           EXPERIMENTAL
 * 
 * 
 *========================================================================**/
export function get_base64_image(img) {
  var canvas = document.createElement("canvas");
  canvas.width = img.width;
  canvas.height = img.height;
  var ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0);
  var dataURL = canvas.toDataURL("image/png");
  return dataURL.replace(/^data:image\/(png|jpg);base64,/, "");
}


/**========================================================================
 * 
 *                          DEBOUNCE
 * 
 * Creates a debounced function that delays invoking the provided function until after a specified delay.
 * @param {Function} func - The function to debounce.
 * @param {number} delay - The number of milliseconds to delay.
 * @return {Function} - The debounced function.
 * 
 *========================================================================**/
export function debounce(func, delay) {
  let debounce_timer;
  return function() {
    const context = this, args = arguments;
    clearTimeout(debounce_timer);
    debounce_timer = setTimeout(() => func.apply(context, args), delay);
  };
}


/**========================================================================
 * 
 *                               DELAY
 * 
 * Delays the execution for a specified duration.
 * @param {number} duration - The duration to delay in milliseconds.
 * @returns {Promise} - A promise that resolves after the specified duration.
 * 
 *========================================================================**/
export function delay(duration) {
  return new Promise(resolve => setTimeout(resolve, duration));
}

/**========================================================================
 * 
 *                           FORMAT PRICE
 * 
 * Formats a price amount based on the currency.
 * This function should be defined in the same scope as extract_title_price_and_image_from_product_variants.
 * @param {number} amount - The price amount in the smallest unit of currency (e.g. Euro Cents)
 * @param {string} currency - The currency code.
 * @param {string} locale - The locale (e.g. 'de-DE')
 * @returns {string} Formatted price string.
 * 
 *========================================================================**/
export function format_price(amount, currency, locale) {
  // IMPORTANT: This conversion assumes that 'amount' is provided in the smallest currency unit (e.g. cents for USD or Euro).
  // Therefore, the amount is divided by 100 to convert it to standard currency format (e.g. converting 1500 cents to 15.00 USD).
  const formattedAmount = amount / 100;
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(formattedAmount);
  } catch {
    // A code Intl does not know (a half-typed 'US', a typo saved on an event)
    // must never take a page or a receipt down with it
    return `${formattedAmount.toFixed(2)} ${String(currency ?? '').toUpperCase()}`.trim();
  }
}

/**========================================================================
 * The value of an amount input: cents as a plain decimal in the locale's
 * notation ("12,50" for de), without currency symbol or grouping.
 * @param {number} amount - in the smallest currency unit
 * @param {string} locale
 * @returns {string} '' when amount is not an integer
 *========================================================================**/
export function format_amount_input(amount, locale) {
  if (!Number.isInteger(amount)) return '';
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(amount / 100);
}

/**========================================================================
 * The reverse: what an admin typed into an amount input, back to cents.
 * Digits and separators only — "1o0" is not an amount. With both separators
 * present the last one is the decimal point ("1.234,50" and "1,234.50" both
 * → 123450). With one kind of separator the locale decides: the locale's
 * grouping character followed by exactly three digits is grouping ("1.000"
 * → 100000 for de, "1,000" for en), anything else is the decimal point
 * ("1,5" and "1.5" → 150 either way). More than two decimals is rejected as
 * ambiguous rather than rounded. null for anything that is not an amount.
 * @param {string} input
 * @param {string} [locale] - decides what a lone separator means
 * @returns {number | null}
 *========================================================================**/
export function parse_amount_input(input, locale = 'en') {
  const trimmed = String(input ?? '').trim();
  if (!trimmed || !/^\d[\d.,]*$/.test(trimmed)) return null;
  let locale_decimal = '.';
  try {
    locale_decimal = new Intl.NumberFormat(locale).formatToParts(1.1).find(part => part.type === 'decimal')?.value ?? '.';
  } catch { /* unknown locale: point */ }
  const last_comma = trimmed.lastIndexOf(',');
  const last_point = trimmed.lastIndexOf('.');
  let decimal_sep = null;
  if (last_comma !== -1 && last_point !== -1) {
    decimal_sep = last_comma > last_point ? ',' : '.';
  } else if (last_comma !== -1 || last_point !== -1) {
    const sep = last_comma !== -1 ? ',' : '.';
    const repeated = trimmed.split(sep).length > 2;
    const digits_after = trimmed.length - Math.max(last_comma, last_point) - 1;
    const is_grouping = repeated || (sep !== locale_decimal && digits_after === 3);
    decimal_sep = is_grouping ? null : sep;
  }
  const grouping_sep = decimal_sep === ',' ? '.' : decimal_sep === '.' ? ',' : (last_comma !== -1 ? ',' : '.');
  const [whole_raw, fraction = ''] = decimal_sep ? trimmed.split(decimal_sep) : [trimmed, ''];
  // Grouping, when present, must group by thousands ("1.2.3" is not an amount)
  if (whole_raw.includes(grouping_sep)) {
    const groups = whole_raw.split(grouping_sep);
    if (!/^\d{1,3}$/.test(groups[0]) || !groups.slice(1).every(group => /^\d{3}$/.test(group))) return null;
  }
  const whole = whole_raw.split(grouping_sep).join('');
  if (!/^\d+$/.test(whole) || !/^\d{0,2}$/.test(fraction)) return null;
  return Number.parseInt(whole, 10) * 100 + Number.parseInt(fraction.padEnd(2, '0'), 10);
}

/**========================================================================
 * The unit next to an amount input: "€" for EUR, "CHF" for CHF — whatever
 * the locale prints for the currency. Falls back to the code.
 * @param {string} currency - ISO 4217 code
 * @param {string} locale
 * @returns {string}
 *========================================================================**/
export function format_currency_symbol(currency, locale) {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency })
      .formatToParts(0)
      .find(part => part.type === 'currency')?.value ?? currency;
  } catch {
    return currency;
  }
}

/**========.================================================================
 * @param {HTMLElement} element 
 *========================================================================**/
export function is_cursor_at_start(element) {
  if (!element) return false;
  const selection = window.getSelection();
  if (!selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  const pre_caret_range = range.cloneRange();
  pre_caret_range.selectNodeContents(element);
  pre_caret_range.setEnd(range.startContainer, range.startOffset);
  return pre_caret_range.toString().length === 0;
}

/**========================================================================
 * @param {HTMLElement} element 
 *========================================================================**/
export function is_cursor_at_end(element) {
  if (!element) return false;
  const selection = window.getSelection();
  if (!selection.isCollapsed) return false;
  const range = selection.getRangeAt(0);
  const post_caret_range = range.cloneRange();
  post_caret_range.selectNodeContents(element);
  post_caret_range.setStart(range.endContainer, range.endOffset);
  return post_caret_range.toString().length === 0;
}

/**========================================================================
 * @param {HTMLElement} element 
 *========================================================================**/
export function set_cursor_at_start(element) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.setStart(element, 0);
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

/**========================================================================
 * @param {HTMLElement} element 
 *========================================================================**/
export function set_cursor_at_end(element) {
  const range = document.createRange();
  const sel = window.getSelection();
  range.selectNodeContents(element);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}


/**========================================================================
 * Finds the next or previous focusable element.
 * 
 * @param {Element} current_element - The currently focused focusable element.
 * @param {'next'|'previous'} target - Specifies whether to find the next or previous element.
 * @param {string} selector_qeury - the selector query e.g. '[tabindex="0"]'
 * @returns {Element | null} - The next or previous focusable element or null if there isn't one.
 * 
 *========================================================================**/
export function find_next_or_previous_element(current_element, target, selector_qeury) {
  // TODO: Also include <input> fields
  const editable_elements = Array.from(document.querySelectorAll(selector_qeury));
  const current_index = editable_elements.indexOf(current_element);
  const delta = target === 'next' ? 1 : -1;
  const target_index = current_index + delta;
  // Check if the target index is within the range of available elements
  if (target_index >= 0 && target_index < editable_elements.length) {
    return editable_elements[target_index];
  }
  return null;
}

/**========================================================================
 * 
 *                    FOCUS END OF PREVIOUS TEXT NODE
 * 
 *========================================================================**/
export function focus_end_of_previous_text_node() {
  const active_element = document.activeElement;
  const previous_element = find_next_or_previous_element(active_element,'previous','[contenteditable="true"][tabindex="0"]');
  if (previous_element) {
    previous_element.focus();
    set_cursor_at_end(previous_element);
  }
}

/**========================================================================
 * 
 *                    FOCUS START OF NEXT TEXT NODE
 * 
 *========================================================================**/
export function focus_start_of_next_text_node() {
  const active_element = document.activeElement;
  const next_element = find_next_or_previous_element(active_element,'next','[contenteditable="true"][tabindex="0"]');
  if (next_element) {
    next_element.focus();
    set_cursor_at_start(next_element);
  }
}

export function is_float(n){
  return Number(n) === n && !Number.isInteger(n);
}


/**========================================================================
 * 
 * Generates an array of values with a non-linear distribution between a start and end value, applying a curvature factor to the linear interpolation.
 * 
 * @param {number} [num_values=15] - The number of values to generate in the array.
 * @param {number} start_value - The starting value of the range.
 * @param {number} end_value - The ending value of the range.
 * @param {number} [curvature_factor=2] - The factor to apply to the curvature; higher values produce more pronounced curvature.
 * @returns {number[]} An array of numbers representing the curved distribution from start_value to end_value.
 * 
 *========================================================================**/
export function plot_curved_distribution(start_value, end_value, num_values = 15, curvature_factor = 4) {
  const result = new Array(num_values);
  const range = end_value - start_value;

  for (let i = 0; i < num_values; i++) {
    // Linear interpolation factor
    const t = i / (num_values - 1);
    // Non-linear transformation for curvature
    const curved_t = Math.pow(t, curvature_factor);
    // Calculate the value at this point in the curve, ensuring it's between start_value and end_value
    let value = start_value + curved_t * range;
    value = parseFloat(value.toFixed(3));
    result[i] = value;
  }
  return result;
}


// We still need to catch pressing ENTER (no newline should be inserted) and possibly other things
export function on_paste(e) {
  // Cancel paste
  e.preventDefault();
  // Get text representation of clipboard
  var text = (e.originalEvent || e).clipboardData.getData('text/plain').replace(/\r?\n|\r/g, '');
  // Insert text manually
  document.execCommand('insertText', false, text);
}

/**========================================================================
 * 
 * Extracts the filename and type from a path or url. Works with both full paths/URLs and simple filenames.
 * Only considers a segment as a filename if it contains a dot followed by a reasonable file extension.
 * @param {string} url - The URL, path, or filename from which to extract the file name and type
 * @returns {{name: string, file_extension: string}} - The file name and file type, if found; otherwise, empty strings
 * 
 *========================================================================**/
export function get_file_name_and_type(url) {
  // Exclude URLs with schemes like 'mailto:' and 'tel:'
  if (!url || url.startsWith('mailto:') || url.startsWith('tel:')) {
    return { name: '', file_extension: '' };
  }

  // Remove query parameters and fragments to isolate the path
  const path = url.split('?')[0].split('#')[0];
  const segments = path.split('/');
  const potentialFilename = segments.pop(); // Get the last segment that could be a filename

  // Look for a period that separates the name and extension, ensuring the extension is of a plausible length
  const lastDotIndex = potentialFilename.lastIndexOf('.');
  if (lastDotIndex > 0 && lastDotIndex < potentialFilename.length - 2 && potentialFilename.length - lastDotIndex <= 5) {
    const name = potentialFilename.substring(0, lastDotIndex);
    const file_extension = potentialFilename.substring(lastDotIndex + 1);

    // Check if the extension consists of only alphanumeric characters
    if (file_extension.match(/^[a-zA-Z0-9]+$/)) {
      return { name, file_extension };
    }
  }

  // Return empty strings if no valid filename and extension are identified
  return { name: '', file_extension: '' };
}



/**
 * Renders micro markdown by applying various formatting rules to the input text.
 * For example: add ['link'] to only render the link title, but the <a> tag.
 * 
 * @param {string} text - The input text to be formatted.
 * @param {Array<'newline'|'bold'|'italic'|'link'|'uppercase'> | 'ignore-all'} [formats_to_ignore=[]] - An array of format names to ignore, or 'ignore-all' to ignore all formatting and return plain text without any markdown annotations.
 * @param {string} [text_style] - The text style to apply to the formatted text.
 * @returns {string} The formatted text with markdown applied.
 */
export function render_micro_markdown(text = '', formats_to_ignore = [], text_style) {

  if (!text) return '';

  /**
    Other markdown props we may want to parse:
    - Blockquotes (e.g., > This is a quote)
    - Unordered lists (e.g., - Item 1, Item 2)
    - Ordered lists (e.g., 1. First item, 2. Second item)
    - Inline code (e.g., code)
    - Horizontal rules (e.g., ---)
    - Strikethrough text (e.g., ~~strikethrough~~)
    - Task lists (e.g., - [ ] Todo item, - [x] Completed item)
    - Definition lists (e.g., Term : Definition)
    - Superscript (e.g., X^2^)
    - Subscript (e.g., H~2~O)
    - Highlight (e.g., ==highlighted text==)
    */

  /**
   * Bullet markers. Markdown's own `-`, `*` and `+`, plus the glyphs that arrive with pasted text:
   * word processors and chat assistants emit `•`, and German authors set lists with an en or em
   * dash. A marker only counts when whitespace follows it, so `*kursiv*` opening a line and `-5 °C`
   * stay prose.
   *
   * The hyphen is escaped rather than parked at the end of the string: the constant is interpolated
   * into character classes that append their own characters after it, where an unescaped trailing
   * `-` would become a range operator instead of a literal.
   */
  const BULLET_MARKER_CHARS = '*+•‣·–—\\-';

  /**
   * Authors indent list items and pasted text carries that indentation along, so a leading run of
   * spaces or tabs is skipped in front of every marker instead of disqualifying the line. Nesting
   * is not modelled — an indented item joins the list as a sibling.
   */
  const INDENT = '[ \\t]{0,12}';

  /**
   * Whitespace inside a line: everything `\s` matches except a line break. Pasted text carries
   * no-break and other Unicode spaces after its markers, which still count; a plain `[ \t]` would
   * drop those lists, and `\s` would let a match run on into the next line.
   */
  const SPACE = '[^\\S\\r\\n]';

  const BULLET_ITEM = `${INDENT}[${BULLET_MARKER_CHARS}]${SPACE}+\\S.*`;

  /**
   * A run of bullet items, at least two of them so a single dashed line stays prose. Items may be
   * separated by one blank line: "loose" lists are what assistants and word processors produce,
   * and the author means the same list either way. A bare marker on a line of its own (an item
   * the author left empty) separates items the same way instead of breaking the run.
   */
  const BULLET_LIST_BLOCK = new RegExp(
    `^(${BULLET_ITEM}(?:\\r?\\n(?:${INDENT}[${BULLET_MARKER_CHARS}]?${SPACE}*\\r?\\n)?${BULLET_ITEM})+(?:\\r?\\n|$))`,
    'gm'
  );

  /** The marker and its trailing space, stripped off an item to leave the item's text. */
  const BULLET_ITEM_MARKER = new RegExp(`^${INDENT}[${BULLET_MARKER_CHARS}]${SPACE}*`);

  /**
   * An ordered list marker: `1.`, `1)`, `(1)`, `a)`, `(a)`, `iii)`.
   *
   * A bare letter followed by a dot (`z. B.`) is deliberately NOT a marker — German abbreviations
   * start lines often enough that it would turn running prose into a list.
   */
  const ORDERED_MARKER = '\\((?:\\d{1,3}|[a-z]|[ivx]{2,5})\\)|\\d{1,3}[.)]|(?:[a-z]|[ivx]{2,5})\\)';

  /** Applied to a single line: group 1 is the marker, group 2 the item text. */
  const ORDERED_LIST_MARKER = new RegExp(`^${INDENT}(${ORDERED_MARKER})${SPACE}+(.*)$`, 'i');

  /**
   * A line that must end an ordered list instead of being swallowed as continuation text: a
   * checkmark item, which its own formatter only turns into markup later. Bullet lines are not
   * terminators — a single one is prose that continues the item (see BULLET_LIST_BLOCK), and a run
   * of them was already formatted by the bullet formatter, which runs first, and nests inside the
   * item as markup (see `format_ordered_list()`).
   */
  const ORDERED_LIST_TERMINATOR = new RegExp(`^${SPACE}*[✓✔︎✔️]${SPACE}`);

  /** A bullet list the formatter before this one already turned into markup. */
  const FORMATTED_LIST_LINE = /^<span role="list"/;

  /**
   * Matches from the first ordered marker line to the end of the text. The block is deliberately
   * greedy — which lines actually belong to the list is decided in `format_ordered_list()`, which
   * returns everything after the list unchanged and re-runs itself on that remainder.
   */
  const ORDERED_LIST_BLOCK = new RegExp(`^(${INDENT}(?:${ORDERED_MARKER})${SPACE}+.*(?:\\r?\\n.*)*)`, 'm');

  const marker_kind = (marker) => /\d/.test(marker) ? 'number' : 'letter';

  /**
   * `1.` is also how German writes an ordinal, and those turn up at the start of a line as often as
   * list items do — "1. Vorsitzende Christa Wohriska" over "2. Vorsitzender Gunther Roos" is a board
   * roster, not a numbered list. Only the bare number-dot form is ambiguous that way, so it alone has
   * to prove itself: every item has to close like a clause. A title ("… Christa Wohriska") does not,
   * a statute paragraph ("… ist das Kalenderjahr.") does. `(1)`, `1)`, `a)` and `(a)` are unambiguous
   * and are taken at face value.
   */
  const AMBIGUOUS_ORDINAL_MARKER = /^\d{1,3}\.$/;
  const ENDS_LIKE_A_CLAUSE = /[.!?;:,]$/;

  /**
   * The other way an item can prove itself: a run-in term at its head — `**Individuelle Beratung**:`
   * or `Beratung:`. A roster line ("1. Vorsitzende Christa Wohriska") has no such head, so items
   * written this way are a list even when they do not close like a clause.
   */
  const HAS_RUN_IN_TERM = /^(?:[^:\n]{1,40}:|\*\*[^*\n]+\*\*)[^\S\r\n]+\S/;

  /**
   * Turns a run of ordered list items into markup, e.g. legal texts numbered `(1) … (2) …`.
   *
   * Markers are rendered verbatim rather than as CSS counters: in statutes and terms of service the
   * numbering is referenced elsewhere ("§ 3 Abs. 2"), so it must survive exactly as authored, gaps
   * and mixed styles included.
   *
   * Lines without a marker continue the item above them, which is how these documents are written —
   * a numbered clause followed by an unnumbered paragraph that belongs to it. Trailing unmarked
   * paragraphs after the last item are the exception: they read as prose closing the section, not as
   * part of the final item, so they are handed back as regular text.
   */
  const format_ordered_list = (block) => {
    // A bullet list formatted before this ran consumed the newline after it, so the line that
    // followed is glued onto its markup; split it off again so its marker is seen. A list ends
    // with its last item's `</span>` followed by its own — item text is escaped, so that pair
    // cannot occur earlier.
    const lines = block.split(/\r?\n/).flatMap((line) => {
      const pieces = [];
      let rest = line;
      let glued;
      while ((glued = /^(<span role="list".*?<\/span><\/span>)(.+)$/.exec(rest))) {
        pieces.push(glued[1]);
        rest = glued[2];
      }
      return [...pieces, rest];
    });
    const items = [];
    let leftover_index = lines.length;
    // Index of the blank line that opened the current gap, so a paragraph can be handed back
    // together with the blank line that separates it from the list.
    let gap_index = null;

    for (let index = 0; index < lines.length; index++) {
      const line = lines[index];
      if (!line.trim()) {
        if (gap_index === null) gap_index = index;
        continue;
      }
      if (ORDERED_LIST_TERMINATOR.test(line)) {
        leftover_index = gap_index ?? index;
        break;
      }
      const item_match = line.match(ORDERED_LIST_MARKER);
      if (item_match) {
        items.push({ marker: item_match[1], parts: [{ text: item_match[2].trim(), gap_index: null }] });
      } else if (items.length) {
        // An already formatted bullet list nests inside the item as markup rather than as text
        items[items.length - 1].parts.push({ text: line.trim(), gap_index, raw: FORMATTED_LIST_LINE.test(line) });
      }
      gap_index = null;
    }

    if (items.length < 2) return block;

    // A blank-line separated paragraph trailing the *last* item is prose, not list content.
    const last_parts = items[items.length - 1].parts;
    const trailing = last_parts.findIndex(part => part.gap_index !== null);
    if (trailing > 0) {
      leftover_index = Math.min(leftover_index, last_parts[trailing].gap_index);
      last_parts.length = trailing;
    }

    // Checked after the trim above, so the test sees where each item actually ends — its last
    // line of text, not the markup of a nested list.
    const last_text = (item) => [...item.parts].reverse().find(part => !part.raw)?.text ?? '';
    if (AMBIGUOUS_ORDINAL_MARKER.test(items[0].marker)
      && !items.every(item => ENDS_LIKE_A_CLAUSE.test(last_text(item)))
      && !items.every(item => HAS_RUN_IN_TERM.test(item.parts[0].text))) {
      return block;
    }

    const marker_width = Math.max(...items.map(item => item.marker.length)) + 1;
    const top_level_kind = marker_kind(items[0].marker);

    const formatted_items = items.map(({ marker, parts }) => {
      const [first, ...continuations] = parts;
      const body = continuations.reduce((html, part) => html + (part.raw
        ? part.text
        : part.gap_index !== null
          ? `<span class="list-paragraph">${escape_html_keeping_entities(part.text)}</span>`
          : `<br>${escape_html_keeping_entities(part.text)}`), escape_html_keeping_entities(first.text));
      // Letter markers below a numbered list are a sub level, e.g. `(3) … a) … b) …`
      const is_sub_item = marker_kind(marker) !== top_level_kind;
      return [
        `<span role="listitem" class="list-item ${is_sub_item ? 'sub-item ' : ''}${text_style} leading-trim">`,
          `<span class="list-marker">${escape_html(marker)}</span>`,
          body,
        '</span>'
      ].join('');
    }).join('');

    const leftover = lines.slice(leftover_index).join('\n');
    return `<span role="list" class="markdown-list ordered-list" style="--list-marker-width: ${marker_width}ch">${formatted_items}</span>`
      + leftover.replace(ORDERED_LIST_BLOCK, format_ordered_list);
  };

  // NOTE: The order of the formatters is important. For example, the definition list formatter must come before the newline formatter.
  const MICRO_MARKDOWN_FORMATTERS = [
    {
      name: 'regular_list',
      // Blank lines inside the block separate items rather than belonging to one, so they drop out
      // instead of producing empty <span>s.
      regex: BULLET_LIST_BLOCK,
      replacement: (match) => {
        const items = match.split(/\r?\n/)
          .map(line => line.replace(BULLET_ITEM_MARKER, '').trim())
          .filter(Boolean);
        const formatted_items = items.map(item => {
          return `<span role="listitem" class="list-item ${text_style} leading-trim">${escape_html_keeping_entities(item)}</span>`;
        }).join('');
        return formatted_items ? `<span role="list" class="markdown-list bullet-list">${formatted_items}</span>` : match;
      },
      ignore_replacement: '$1',
    },
    // After the bullet list, so a bullet list under a numbered item arrives already formatted and
    // nests inside the item as markup (see `format_ordered_list()`).
    {
      name: 'ordered_list',
      regex: ORDERED_LIST_BLOCK,
      replacement: format_ordered_list,
      ignore_replacement: '$1',
    },
    // List types must be applied before the newline formatter
    {
      name: 'definition_list',
      /**
       * Regex explanation:
       * ^                   - Start of line
       * (                   - Start capturing group for the entire list
       *   (?:               - Start non-capturing group for each item
       *     (?!             - Negative lookahead to exclude lines starting with:
       *       [•…✓✔︎✔️-]     - Any bullet or checkmark marker
       *       [ \t]         - Followed by a space or tab
       *     )
       *     [^\n:]{1,30}:   - Match 1 to 30 characters up to a colon (term)
       *     [ \t]+.+        - Match one or more characters after the colon (definition)
       *     (?:\r?\n|$)     - Match a newline or end of string
       *   ){2,}             - End non-capturing group, repeat at least twice (ensures at least 2 items)
       * )                   - End capturing group
       *
       * The lookahead is inside the repeat, not in front of the block: it has to keep a bullet or
       * checkmark line out of the *middle* of a definition list too, or a lone `- Punkt: ja` under
       * two `Term: Wert` pairs gets pulled in as a third pair.
       *
       * The space after the colon is SPACE (no line breaks), not `\s+`, so a term whose value sits on the next
       * line ("Öffnungszeiten:\nMo–Fr") cannot reach across the newline and swallow whatever
       * follows — which is how list lines below such a term ended up as definitions.
       */
      regex: new RegExp(`^((?:(?![${BULLET_MARKER_CHARS}✓✔︎✔️]${SPACE})[^\\n:]{1,30}:${SPACE}+.+(?:\\r?\\n|$)){2,})`, 'gm'),
      replacement: (match) => {
        const items = match.trim().split(/\r?\n/);
        const formatted_items = items.map(item => {
          const colonIndex = item.indexOf(':');
          if (colonIndex === -1 || colonIndex > 30) return '';
          const term = escape_html_keeping_entities(item.slice(0, colonIndex).trim());
          const definition = escape_html_keeping_entities(item.slice(colonIndex + 1).trim());
          return `<span role="term" class='definition-term ${text_style} leading-trim'>${term}</span><span role="definition" class='definition-desc ${text_style} leading-trim'>${definition}</span>`;
        }).filter(Boolean).join('');
        return formatted_items ? `<span role="list" class='markdown-list definition-list'>${formatted_items}</span>` : match;
      },
      ignore_replacement: '$1',
    },
    {
      name: 'checkmark_list',
      /**
       * Regex explanation:
       * ^                   - Start of line
       * (                   - Start capturing group for the entire list
       *   (?:               - Start non-capturing group for each item
       *     [✓✔︎✔️]\s+        - Match any checkmark symbol followed by whitespace
       *     .+              - Match one or more characters (the list item text)
       *     (?:\r?\n|$)     - Match a newline or end of string
       *   )+                - End non-capturing group, repeat one or more times
       * )                   - End capturing group
       */
      regex: /^((?:[✓✔︎✔️]\s+.+(?:\r?\n|$))+)/gm,
      replacement: (match) => {
        const items = match.trim().split(/\r?\n/);
        const checkmark_count = items.filter(item => /^[✓✔︎✔️]\s+/.test(item)).length;
        if (checkmark_count < 2) return match;
        
        const formatted_items = items.map(item => {
          const text = escape_html_keeping_entities(item.replace(/^[✓✔︎✔️]\s+/, '').trim());
          return [
            '<span role="listitem" class="checkmark flex-row gap-x g-3 items-center">', 
              '<svg class="icon" role="presentation"><use href="/assets/sprite-latest.svg#check"></use></svg>',
              `<span class="${text_style}">`,
                text, 
              '</span>',
            '</span>'
          ].join('');
        }).filter(Boolean).join('');
        return formatted_items ? `<span role="list" class="markdown-list checkmark-list">${formatted_items}</span>` : match;
      },
      ignore_replacement: '$1',
    },
    // Apply link parsing after list parsing so links inside list items remain clickable
    // and are not escaped into plain text by list formatters.
    //
    // A link inside a list item arrives with its text already escaped, so the title keeps the
    // entities it carries and the href is decoded before it is checked and escaped once — the
    // browser then follows exactly the URL that was checked, and `&` in a query string is not
    // doubled into `&amp;amp;`.
    {
      name: 'link',
      regex: /\[(.*?)\]\((.*?)\)/g,
      replacement: (match, title, raw_href) => {
        const href = decode_html_entities(raw_href);
        if (is_script_link_href(href)) return escape_html_keeping_entities(title);
        const safe_href = escape_html(href);
        const safe_title = escape_html_keeping_entities(title);
        const is_external = is_external_url(href);
        // No target on internal links: SvelteKit treats any target, `_self` included, as an
        // external link and reloads the whole document instead of navigating in place
        return `<a href="${safe_href}" class="${is_external ? 'external' : 'internal'}"${is_external ? ' target="_blank"' : ''}>${safe_title}</a>`;
      },
      ignore_replacement: '$1 ($2)', // Preserve full link syntax when ignored, but don't wrap the text
    },
    // Newline formatter must be applied after any formatter using newlines
    {
      name: 'newline',
      regex: /\r\n|\r|\n|\\n/g,
      replacement: '<br>',
      ignore_replacement: ' ', // Replace newlines with a space when ignored
    },
    {
      name: 'bold',
      regex: /\*\*(?![\*\s])([^\*]*[^\*\s])?\*\*/g,
      replacement: '<strong>$1</strong>',
      ignore_replacement: '$1', // Only preserve the content when ignored
    },
    {
      name: 'italic',
      // Works also with German gender-inclusive language (Gendersternchen), for example: "Arbeiter*innen"
      regex: /([_*])(?<!(?:\1|\w).)(?![_*\s])(.*?[^_*\s])(?=\1)([_*])(?!\w|\3)/g,
      replacement: '<em>$2</em>',
      ignore_replacement: '$2', // Only preserve the content when ignored
    },
    // This is non-standard markdown to make a text uppercase by wrapping it with ++my text++
    // The issue with writing text in actual uppercase letters is that in contexts like search or entry embeds we want regular casing
    // This format allows to set uppercase text without affecting the actual casing of the text
    {
      name: 'uppercase',
      regex: /\+\+(?!\s)([^\+]*[^\s])?\+\+/g,
      replacement: '<em class="-caps">$1</em>',
      ignore_replacement: '$1', // Only preserve the content when ignored
    },
    // This is non-standard markdown to make a text small caps by wrapping it with ^^my text^^
    {
      name: 'smallcaps',
      regex: /\^\^(?!\s)([^\^]*[^\s])?\^\^/g,
      replacement: '<em class="-smallcaps">$1</em>',
      ignore_replacement: '$1',
    },
    {
      name: 'highlight',
      regex: /==(?!==)((?:[^=\n]|(?<!=)=)+?)==(?!=)/g,
      replacement: '<mark>$1</mark>',
      ignore_replacement: '$1', // Only preserve the content when ignored
    },
  ];

  if(Array.isArray(formats_to_ignore) && formats_to_ignore.length === MICRO_MARKDOWN_FORMATTERS.length) return text;

  const strip_all_formatting = formats_to_ignore === 'ignore-all';

  MICRO_MARKDOWN_FORMATTERS.forEach(({ name, regex, replacement, ignore_replacement }) => {
    const should_apply = !strip_all_formatting && !formats_to_ignore.includes(name);
    text = text.replace(regex, should_apply ? replacement : ignore_replacement);
  });

  return text;
}

/**
 * Renders micro markdown for text that must not bring its own markup: messages any member writes,
 * and e-mail bodies. render_micro_markdown lets HTML through on purpose, for the entities authors
 * type into entry text; here entities are decoded and everything is escaped before formatting, so
 * `&shy;` or `&amp;` still become their character while `<img onerror=…>` stays text.
 *
 * @param {string} text - The text to render.
 * @param {Parameters<typeof render_micro_markdown>[1]} [formats_to_ignore=[]] - As for render_micro_markdown.
 * @param {string} [text_style] - As for render_micro_markdown.
 * @returns {string} The formatted text, safe for `{@html}`.
 */
export function render_safe_micro_markdown(text = '', formats_to_ignore = [], text_style) {
  return render_micro_markdown(escape_html(decode_html_entities(String(text ?? ''))), formats_to_ignore, text_style);
}

  // TODO: Option A) Pre-compile the formatted markdown tokens into a single string and store it in the database.
  // TODO: Option B) Use svedit and not markdown anymore, directly render tokens from JSON
  // TOKEN BASED APPROACH

  // // NOTE: The order of the formatters is important. For example, the definition list formatter must come before the newline formatter.
  // const MICRO_MARKDOWN_FORMATTERS = [
  //   {
  //     name: 'link',
  //     regex: /\[(.*?)\]\((.*?)\)/g,
  //     process: (match, groups) => {
  //       const [_, title, href] = groups;
  //       const is_external = is_external_url(href);
  //       return {
  //         type: 'link',
  //         content: title,
  //         props: {
  //           href,
  //           class: is_external ? 'external' : 'internal',
  //           target: is_external ? '_blank' : '_self'
  //         }
  //       };
  //     },
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: `${match[1]} (${match[2]})`
  //     })
  //   },
  //   // List types must be applied before the newline formatter
  //   {
  //     name: 'checkmark_list',
  //     regex: /^((?:[✓✔︎✔️]\s+.+(?:\r?\n|$))+)/gm,
  //     process: (match) => {
  //       const items = match[1].trim().split(/\r?\n/);
  //       const checkmark_count = items.filter(item => /^[✓✔︎✔️]\s+/.test(item)).length;
        
  //       if (checkmark_count < 2) return { type: 'text', content: match[0] };
        
  //       return {
  //         type: 'checkmark_list',
  //         items: items.map(item => ({
  //           content: item.replace(/^[✓✔︎✔️]\s+/, '').trim()
  //         }))
  //       };
  //     },
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: match[1]
  //     })
  //   },
  //   {
  //     name: 'definition_list',
  //     regex: /^((?:.{1,30}?:\s+.+(?:\r?\n|$)){2,})/gm,
  //     process: (match) => {
  //       const items = match[1].trim().split(/\r?\n/);
  //       return {
  //         type: 'definition_list',
  //         items: items.map(item => {
  //           const colonIndex = item.indexOf(':');
  //           if (colonIndex === -1 || colonIndex > 30) return null;
  //           return {
  //             term: item.slice(0, colonIndex).trim(),
  //             definition: item.slice(colonIndex + 1).trim()
  //           };
  //         }).filter(Boolean)
  //       };
  //     },
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: match[1]
  //     })
  //   },
  //   // Newline formatter must be applied after any formatter using newlines
  //   {
  //     name: 'newline',
  //     regex: /\r\n|\r|\n|\\n/g,
  //     process: () => ({ type: 'newline' }),
  //     ignore: () => ({
  //       type: 'text',
  //       content: ' '
  //     })
  //   },
  //   {
  //     name: 'bold',
  //     regex: /\*\*(?![\*\s])([^\*]*[^\*\s])?\*\*/g,
  //     process: (match) => ({
  //       type: 'bold',
  //       content: match[1]
  //     }),
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: match[1]
  //     })
  //   },
  //   {
  //     // Works also with German gender-inclusive language (Gendersternchen), for example: "Arbeiter*innen"
  //     name: 'italic',
  //     regex: /([_*])(?<!(?:\1|\w).)(?![_*\s])(.*?[^_*\s])(?=\1)([_*])(?!\w|\3)/g,
  //     process: (match) => ({
  //       type: 'italic',
  //       content: match[2]
  //     }),
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: match[2]
  //     })
  //   },
  //   // This is non-standard markdown to make a text uppercase by wrapping it with ++my text++
  //   // The issue with writing text in actual uppercase letters is that in contexts like search or entry embeds we want regular casing
  //   // This format allows to set uppercase text without affecting the actual casing of the text
  //   {
  //     name: 'uppercase',
  //     regex: /\+\+(?!\s)([^\+]*[^\s])?\+\+/g,
  //     process: (match) => ({
  //       type: 'uppercase',
  //       content: match[1]
  //     }),
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: match[1]
  //     })
  //   },
  //   // This is non-standard markdown to make a text small caps by wrapping it with ^^my text^^
  //   {
  //     name: 'smallcaps',
  //     regex: /\^\^(?!\s)([^\^]*[^\s])?\^\^/g,
  //     process: (match) => ({
  //       type: 'smallcaps',
  //       content: match[1]
  //     }),
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: match[1]
  //     })
  //   },
  //   {
  //     name: 'highlight',
  //     regex: /==(?!==)((?:[^=\n]|(?<!=)=)+?)==(?!=)/g,
  //     process: (match) => ({
  //       type: 'highlight',
  //       content: match[1]
  //     }),
  //     ignore: (match) => ({
  //       type: 'text',
  //       content: match[1]
  //     })
  //   }
  // ];

  // /**
  //  * Renders micro markdown by applying various formatting rules to the input text.
  //  * @param {string} input - The input text to be formatted
  //  * @param {Object} options - Options object
  //  * @param {Array<'newline'|'bold'|'italic'|'link'|'uppercase'|'smallcaps'|'highlight'>} [options.formats_to_ignore=[]] - Array of format names to ignore
  //  * @returns {Array<{type: string, content: string, props?: Object}>} Array of parsed markdown tokens
  //  */
  // export function render_micro_markdown(input, { formats_to_ignore = [] } = {}) {
  //   if (!input) return [{ type: 'text', content: '' }];

  //   const tokens = [];
  //   let current_pos = 0;
  //   let text = input;

  //   while (current_pos < text.length) {
  //     let earliest_match = null;
  //     let earliest_formatter = null;
  //     let earliest_position = text.length;

  //     for (const formatter of MICRO_MARKDOWN_FORMATTERS) {
  //       formatter.regex.lastIndex = current_pos;
  //       const match = formatter.regex.exec(text);
  //       if (match && match.index < earliest_position) {
  //         earliest_position = match.index;
  //         earliest_match = match;
  //         earliest_formatter = formatter;
  //       }
  //     }

  //     if (earliest_match) {
  //       // Add text before match
  //       if (earliest_position > current_pos) {
  //         tokens.push({
  //           type: 'text',
  //           content: text.slice(current_pos, earliest_position)
  //         });
  //       }

  //       // Process match based on whether it should be ignored
  //       const should_ignore = formats_to_ignore.includes(earliest_formatter.name);
  //       tokens.push(
  //         should_ignore 
  //           ? earliest_formatter.ignore(earliest_match)
  //           : earliest_formatter.process(earliest_match, earliest_match)
  //       );
        
  //       current_pos = earliest_position + earliest_match[0].length;
  //     } else {
  //       // Add remaining text
  //       tokens.push({
  //         type: 'text',
  //         content: text.slice(current_pos)
  //       });
  //       break;
  //     }
  //   }

  //   return tokens;
  // }


/**========================================================================
 * Prettifies the given file name by removing dashes, numbers at the end, converting to title case.
 * @param {string} file_name - The original file name.
 * @returns {string | null} - The prettified file name or null if the name seems random.
 *========================================================================**/
export function prettify_file_name(file_name) {
  if (/^[a-zA-Z]+$/.test(file_name)) { // Checks for filenames that seem random
    return null;
  }

  return file_name
    .replace(/(?<=\w)[_\-–—](?=\w)/g, ' ') // Replace all occurrences of dashes and/or underscores with spaces. But only replace if there is a letter or number before and after
    .trim() // Remove leading and trailing whitespaces
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()) // Convert to title case
    .join(' ');
}



/**========================================================================
 * 
 * Converts a string representation of a number, fraction, or float into a float value.
 * @param {string} str - The string to convert.
 * @returns {number|null} The converted float value, or null if conversion is not possible.
 * 
 *========================================================================**/
export function convert_string_to_float(str) {
  // Check if the string is directly a valid number (integer or float)
  if (!isNaN(str) && str !== '') {
    return parseFloat(str);
  }

  str = str.trim();

  // Handle fraction (e.g. "1/2")
  const parts = str.split('/');
  if (parts.length === 2) {
    const numerator = parseFloat(parts[0].trim());
    const denominator = parseFloat(parts[1].trim());
    // Check if both parts are valid numbers and denominator is not zero
    if (!isNaN(numerator) && !isNaN(denominator) && denominator !== 0) {
      return numerator / denominator;
    }
  }

  // invalid input
  return null;
}


/**========================================================================
 * 
 * Removes leading and trailing whitespaces and newlines from the given string.
 * 
 * @param {string} str - The content to be trimmed.
 * @returns {string} The trimmed content or an empty string.
 *========================================================================**/
export function trim_whitespaces(str) {
  if (!str) return '';
  // Trim whitespace and newlines from both ends
  return str.replace(/^[\s\n]+|[\s\n]+$/g, '');
}


/**
 * Selects the optimal contrast color from a list of colors based on the greatest lightness difference from a given background lightness.
 *
 * @param {number} background_lightness - The lightness value of the background against which contrast is measured.
 * @param {Array<{ lightness: number }>} colors - An array of color objects, each with a `lightness` property.
 * @returns {{ lightness: number }} - The color object with the greatest contrast against the background.
 */
export function select_optimal_contrast_color(background_lightness, colors) {
  const dark_color = colors[0]; // The first element is the darkest
  const light_color = colors[colors.length - 1]; // The last element is the lightest
  // Calculate the absolute difference in lightness between the background and both the darkest and lightest colors
  const contrast_with_dark = Math.abs(background_lightness - dark_color.lightness);
  const contrast_with_light = Math.abs(background_lightness - light_color.lightness);
  // Choose the color that has the greater contrast (larger difference in lightness) with the background
  return contrast_with_dark > contrast_with_light ? dark_color : light_color;
}


/**========================================================================
 * 
 * Sort keys based on a predefined order.
 * @param {Array<string>} keys - The array of keys to sort.
 * @param {Array<string>} order - The array defining the predefined order.
 * @returns {Array<string>} - The sorted array of keys.
 * 
*========================================================================**/
export function sort_keys_with_order(keys, order) {
  return keys.sort((a, b) => {
    const indexA = order.indexOf(a);
    const indexB = order.indexOf(b);

    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    } else if (indexA !== -1) {
      return -1;
    } else if (indexB !== -1) {
      return 1;
    } else {
      return String(a).localeCompare(String(b));
    }
  });
}


/**========================================================================
 * 
 * Determines the orientation of an image based on width and height.
 * @param {number} width - The width of the image.
 * @param {number} height - The height of the image.
 * @param {number} [square_threshold=0.1] - The threshold for determining if the aspect ratio still counts as square.
 * @returns {string} - Returns 'portrait', 'landscape', or 'square'.
 * 
*========================================================================**/
export function determine_orientation(width, height, square_threshold = 0.1) {
  const aspect_ratio = width / height;

  // Determine if the aspect ratio is close enough to 1 to be considered square
  if (Math.abs(aspect_ratio - 1) <= square_threshold) {
    return 'square';
  } else if (aspect_ratio < 1) {
    return 'portrait';
  } else {
    return 'landscape';
  }
}

/**========================================================================
 * 
 * Generates a list of site color options for »Select Inputs« based on the provided color configuration.
 * Each option includes a title, value, and various styles for icons and backgrounds.
 *
 * @param {Array<import('./SiteSettingsColorModel.svelte.js').color_set>} site_color_array - The array of color sets from SiteSettingsColorModel.
 * @returns {{
 * title: string, 
 * value: string, 
 * icon: string, 
 * icon_color1: string, 
 * icon_color2: string, 
 * background_color: string,
 * id: string
 * }[]} 
 * 
 *========================================================================**/
export function get_site_color_options(site_color_array) {
  // Ensure input is an array
  if (!Array.isArray(site_color_array)) {
    console.error('get_site_color_options expected an array, received:', site_color_array);
    return [];
  }

  return site_color_array.map((color_set) => {
    const is_default = color_set.default === true;
    
    return {
      title: color_set.name,
      // Use empty string ('') as the value for the default set, otherwise use the ID
      value: is_default ? '' : color_set.id,
      // Keep simple icon, color is applied via styles
      icon: 'colored-text', 
      // Pass OKLCH strings directly for CSS usage
      icon_color1: color_set.foreground, // Represents text/icon color
      icon_color2: color_set.background, // Represents background
      background_color: color_set.emphasis_background, // Used for swatch/preview
      id: color_set.id // Include the actual ID for potential lookups if needed
    };
  });
}

/**========================================================================
 * 
 * Generates a string of CSS variables that map generic color roles (--color-background, etc.)
 * to the specific variables of a given theme color (e.g., --color-default-background).
 * This allows components to use generic variables that adapt to the selected theme.
 *
 * @param {string} color_slug - The name of the theme color set (e.g., 'canvas', 'accent_1').
 * @returns {string} A string containing CSS variable assignments for the generic roles based on the theme color.
 * 
 * Example output for color_slug = 'canvas':
 * --color-background: var(--color-${slug_css}-background);
 * --color-foreground: var(--color-${slug_css}-foreground);
 * --color-emphasis-background: var(--color-${slug_css}-emphasis-background);
 * --color-emphasis-foreground: var(--color-${slug_css}-emphasis-foreground);
 * --color-default-emphasis-background: var(--color-${slug_css}-emphasis-background);
 * --color-default-emphasis-foreground: var(--color-${slug_css}-emphasis-foreground);
 * --color-background-lux-mode: var(--color-${slug_css}-background-lux-mode);
 * --color-foreground-lux-mode: var(--color-${slug_css}-foreground-lux-mode);
 * --color-default-background-lux-mode: var(--color-${slug_css}-background-lux-mode);
 * --color-default-foreground-lux-mode: var(--color-${slug_css}-foreground-lux-mode);
 * --color-emphasis-background-lux-mode: var(--color-${slug_css}-emphasis-background-lux-mode);
 * --color-emphasis-foreground-lux-mode: var(--color-${slug_css}-emphasis-foreground-lux-mode);
 * --font-smoothing: var(--${slug_css}-font-smoothing);
 * --text-1/--text-2/--text-3 rebound to the color set foreground
*========================================================================**/
export function generate_css_variables(color_slug) {
  if (!color_slug || color_slug === 'transparent') return '';

  const slug_css = color_slug.replace(/_/g, '-'); // Ensure names like accent_1 become accent-1

  const variables = [
    `--color-background: var(--color-${slug_css}-background)`,    
    `--color-foreground: var(--color-${slug_css}-foreground)`,
    `--color-default-background: var(--color-${slug_css}-background)`,    
    `--color-default-foreground: var(--color-${slug_css}-foreground)`,
    `--color-emphasis-background: var(--color-${slug_css}-emphasis-background)`,
    `--color-emphasis-foreground: var(--color-${slug_css}-emphasis-foreground)`,
    // Keep --color-default-emphasis-* in sync so components that hard-reference the
    // default-prefixed tokens (e.g. SearchSelect hover) still follow the active color set.
    `--color-default-emphasis-background: var(--color-${slug_css}-emphasis-background)`,
    `--color-default-emphasis-foreground: var(--color-${slug_css}-emphasis-foreground)`,
    `--color-background-lux-mode: var(--color-${slug_css}-background-lux-mode)`,
    `--color-foreground-lux-mode: var(--color-${slug_css}-foreground-lux-mode)`,
    `--color-default-background-lux-mode: var(--color-${slug_css}-background-lux-mode)`,
    `--color-default-foreground-lux-mode: var(--color-${slug_css}-foreground-lux-mode)`,
    `--color-emphasis-background-lux-mode: var(--color-${slug_css}-emphasis-background-lux-mode)`,
    `--color-emphasis-foreground-lux-mode: var(--color-${slug_css}-emphasis-foreground-lux-mode)`,
    `--font-smoothing: var(--${slug_css}-font-smoothing)`,
    // Re-bind derived text tokens so muted labels (text-2/text-3) resolve against the
    // remapped foreground instead of a stale :root value.
    `--text-1: var(--color-${slug_css}-foreground)`,
    `--text-2: oklch(from var(--color-${slug_css}-foreground) l c h / 0.7)`,
    `--text-3: oklch(from var(--color-${slug_css}-foreground) l c h / 0.5)`
  ];

  return variables.join('; ');
}

export function is_workflow_expired (workflow) {
  const expires = new Date(workflow.expires);
  if (isNaN(expires.getTime())) return true;
  if (expires.getTime() < Date.now()) return true;
  return false;
}

/**========================================================================
 * 
 * Returns the icon name for a given visibility level.
 * @param {string} visibility - The visibility level.
 * @returns {string} - The icon name.
 * 
 *========================================================================**/
export function get_visibility_icon(visibility){
  if (visibility === 'public') {
    return 'globe';
  } else if (visibility === 'private') {
    return 'lock';
  } else if (visibility === 'member') {
    return 'person-2';
  } else if (visibility === 'internal') {
    return 'building';
  }
}


/**========================================================================
 * 
 * Checks if a string is a valid URL and returns a properly formatted URL.
 * @param {string} str - The string to check.
 * @param {boolean} loose - If true, accepts domain-like strings without protocol (e.g., "example.com").
 * @returns {string|false} - The formatted URL if valid, false otherwise.
 * 
 *========================================================================**/
export function is_url(str, loose = false) {
  str = str.trim();
  
  // Check for www. prefix
  if (str.startsWith('www.')) {
    return `https://${str}`;
  }
  
  // Check for existing protocol
  if (/^https?:\/\//i.test(str)) {
    return str;
  }
  
  // Loose validation: check if it looks like a domain (has at least one dot and valid characters)
  if (loose) {
    // Basic domain pattern: at least one character, a dot, and at least 2 characters for TLD
    // Allows subdomains, hyphens, etc.
    if (/^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/.test(str)) {
      return `https://${str}`;
    }
  }
  
  return false;
}

/**========================================================================
 * 
 * Checks if a URL is external (different origin than current site).
 * @param {string} url - The URL to check.
 * @returns {boolean} - True if the URL is external, false if internal or relative.
 * 
 *========================================================================**/
export function is_external_url(url) {
  if (!url) return false;
  
  // Relative URLs are internal
  if (url.startsWith('/') || url.startsWith('#') || url.startsWith('?')) {
    return false;
  }
  
  // Special protocols (mailto:, tel:, sms:, etc) are not external in the sense of opening new tabs
  if (url.includes(':') && !url.startsWith('http://') && !url.startsWith('https://')) {
    return false;
  }
  
  try {
    const url_obj = new URL(url, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const current_origin = typeof window !== 'undefined' ? window.location.origin : '';
    
    // If we're in a browser context, compare origins
    if (current_origin) {
      return url_obj.origin !== current_origin;
    }
    
    // During SSR, treat absolute URLs as external
    return url.startsWith('http://') || url.startsWith('https://');
  } catch {
    // If URL parsing fails, assume it's internal
    return false;
  }
}

const SAFE_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:', 'sms:']);
const SCRIPT_LINK_PROTOCOLS = new Set(['javascript:', 'vbscript:', 'data:']);

// The scheme a browser resolves href to, or null when it does not parse
function link_protocol(href) {
  try {
    return new URL(href, 'https://relative.invalid').protocol;
  } catch {
    return null;
  }
}

/**
 * Whether an href would run script or open a document when followed. Unlike
 * is_safe_link_href it allows every other scheme (webcal:, whatsapp:, …), for
 * text whose links have always been free-form. Parsed the way a browser does.
 * @param {string} href
 * @returns {boolean}
 */
export function is_script_link_href(href) {
  const protocol = link_protocol(href);
  return protocol === null || SCRIPT_LINK_PROTOCOLS.has(protocol);
}

/**
 * Whether a user-supplied link may be rendered as an href: relative, web,
 * mailto, tel or sms. Parsed the way a browser does, so "java\tscript:" or
 * leading spaces do not slip a javascript: or data: URL past the check.
 * @param {string} href
 * @returns {boolean}
 */
export function is_safe_link_href(href) {
  if (href == null || href === '') return true;
  if (typeof href !== 'string') return false;
  return SAFE_LINK_PROTOCOLS.has(link_protocol(href));
}

/**========================================================================
 * 
 * Simple check if a string is a single, plain email address.
 * Rejects the characters mail address parsers read as syntax (lists, display
 * names, comments, groups, quoting): nodemailer would otherwise send to other
 * or several recipients, and parse a crafted list in quadratic time.
 * @param {string} str - The string to check.
 * @returns {boolean} - True if the string is a valid email address, false otherwise.
 *
 *========================================================================**/
export function is_email(str) {
  const email = str.trim();
  // 254: the longest address SMTP can deliver (RFC 5321)
  return email.length <= 254 && /^[^\s@",:;<>()\\]+@[^\s@",:;<>()\\]+\.[^\s@",:;<>()\\]+$/.test(email);
}

/**========================================================================
 * 
 * Checks if a string is a valid phone number.
 * @param {string} str - The string to check.
 * @returns {boolean} - True if the string is a valid phone number, false otherwise.
 * 
 *========================================================================**/
export function is_phone(str) {
  return /^\+[0-9]{1,3}[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,4}[-\s.]?[0-9]{1,9}$/.test(str.trim());
}

/**========================================================================
 * 
 * Strict check: integer with optional leading minus sign; disallows decimals like "3.0".
 * @param {string|number} value - Value to check
 * @returns {boolean}
 * 
 *========================================================================**/
export function is_strict_integer(value) {
  const str = String(value).trim();
  return /^-?\d+$/.test(str);
}

/**
 * Checks if a string matches the user profile pattern (@username[-id]).
 * @param {string} str - The string to check.
 * @returns {boolean} - True if the string matches the pattern, false otherwise.
 */
export function is_user_profile(str) {
  return /^@[\w-]+$/.test(str.trim());
}

/**
 * Parses an absolute URL (http, https, www) and determines its type and details.
 * @param {string} term - The potential URL string.
 * @param {string} [base_url] - Optional base URL for relative path resolution. If not provided, will use the current origin.
 * @returns {{ type: string, value: string, title: string } | null} - Link details or null if not a valid absolute URL.
 */
export function parse_absolute_url(term, base_url) {
  const formatted_url = is_url(term);
  if (!formatted_url) return null;

  try {
    // If no base_url is provided, we'll use the current origin from the page data
    // This is available in client-side code via $page.data.origin
    const url_object = new URL(formatted_url, base_url);
    
    // Check if it's an internal URL by comparing hostnames
    // We'll consider it internal if it's the same as our base_url hostname
    const is_internal = base_url ? url_object.hostname === new URL(base_url).hostname : false;

    if (is_internal) {
      const relative_path = url_object.pathname + url_object.search + url_object.hash;

      const { name: file_name, file_extension } = get_file_name_and_type(relative_path);
      if (file_name && file_extension && !['html', 'htm'].includes(file_extension.toLowerCase())) {
        return { type: 'relative_path', value: relative_path, title: `${file_name}.${file_extension}` };
      }

      return { type: 'relative_path', value: relative_path, title: relative_path };
    }
    // External URLs
    else {
      const { name: file_name, file_extension } = get_file_name_and_type(url_object.pathname);
      if (file_name && file_extension && !['html', 'htm'].includes(file_extension.toLowerCase())) {
        return { type: 'file', value: formatted_url, title: `${file_name}.${file_extension}` };
      }
      return { type: 'weblink', value: formatted_url, title: url_object.hostname };
    }
  } catch (e) {
    console.error("Error parsing URL:", term, e);
    // Fallback for URLs that passed is_url but failed new URL()
    return { type: 'weblink', value: formatted_url, title: formatted_url };
  }
}

/**
 * Parses an explicit relative path (starts with /) and determines its type and details.
 * @param {string} term - The potential relative path string.
 * @returns {{ type: string, value: string, title: string } | null} - Link details or null if not a valid relative path.
 */
export function is_relative_path(term) {
  return /^\/(?!\/)/.test(term);
}

/**
 * Checks if a term is an in-page anchor (e.g. "#wNUPCEBWEFKPpUVCrTyWD").
 * Blocks render with their block_id as the DOM id (see Block.svelte), so an anchor
 * link is how you jump to another block on the same page.
 * @param {string} term - The potential anchor string.
 * @returns {boolean}
 */
export function is_anchor(term) {
  return /^#[A-Za-z0-9_-]+$/.test(String(term ?? '').trim());
}

/**
 * Checks if a path matches any fixed routes in the application
 * @param {string} term - The path to check
 * @returns {{ type: string, value: string, title: string } | null} - The fixed route details or null if no match
 */
export function parse_fixed_route(term) {
  const fixed_route_regex_map = get_fixed_route_regex_map();
  for (const [routeName, regex] of fixed_route_regex_map.entries()) {
    if (regex.test(term)) {
      return { type: routeName, value: `/${routeName}` };
    }
  }
  return null;
}


/**========================================================================
 * 
 * Determines the type of link from the given text.
 * @param {string} term - The text to analyze.
 * @returns {{
 * type: 'email' | 'phone' | 'weblink' | 'user_profile' | 'messages' | 'settings' | 'calendar' | 'posters' | 'account' | 'login' | 'relative_path' | 'file' | null,
 * value: string | null,
 * title: string | null
 * }} - The link type, formatted value, and title.
 * 
 *========================================================================**/
export function determine_link_type(term) {
  if (!term) {
    return null;
  }
  const trimmed_term = term.trim();

  // 1. Email
  const term_without_mailto = trimmed_term.replace(/^mailto:/, '');
  if (is_email(term_without_mailto)) {
    return { type: 'email', value: `mailto:${term_without_mailto}`, title: term_without_mailto };
  }

  // 2. Phone
  const term_without_tel = trimmed_term.replace(/^tel:/, '');
  if (is_phone(term_without_tel)) {
    return { type: 'phone', value: `tel:${term_without_tel}`, title: term_without_tel };
  }


  // 3. In-page anchor (#block_id) — jumps to another block on the same page
  if (is_anchor(trimmed_term)) {
    return { type: 'anchor', value: trimmed_term, title: trimmed_term.slice(1) };
  }

  // 4. Absolute URLs (http/https/www)
  const absolute_url_result = parse_absolute_url(trimmed_term);
  if (absolute_url_result) {
    return absolute_url_result;
  }

  // 5. User Profile (@username or /@username)
  const term_without_leading_slash = trimmed_term.replace(/^\//, '');
  if (is_user_profile(term_without_leading_slash)) {
    const term_without_at = term_without_leading_slash.replace(/^@/, '');
    const user_name = term_without_at.replace(/-[0-9]+$/, '');
    const value = `/${term_without_leading_slash}`;
    return { type: 'user_profile', value, title: user_name };
  }

  // 6. Messages/ Posters/ Calendar/ Account
  const fixed_routes = {
    messages: { type: 'messages' },
    posters: { type: 'posters' },
    calendar: { type: 'calendar' },
    account: { type: 'account' },
    login: { type: 'login' }
  };

  // Extract base path without query params or hash
  const base_path = term_without_leading_slash.split(/[?#]/)[0];
  
  if (fixed_routes[base_path]) {
    const route = { ...fixed_routes[base_path] };
    route.value = `/${term_without_leading_slash}`; // Preserve full path including query params and hash
    return route;
  }

  // 7. Explicit Relative Paths (Starts with /)
  if (is_relative_path(trimmed_term)) {
    const fixed_route = parse_fixed_route(trimmed_term);
    if (fixed_route) {
      return fixed_route;
    }
    // Check for file paths within explicit relative paths
    const { name: file_name, file_extension } = get_file_name_and_type(trimmed_term);
    if (file_name && file_extension && !['html', 'htm'].includes(file_extension.toLowerCase())) {
        // It's a file link if it has a non-html extension
        return { type: 'file', value: trimmed_term, title: `${file_name}.${file_extension}` };
    }

    // If it starts with / but isn't a fixed route or file, treat as generic relative path
    // We might want to check if it corresponds to an entry slug here in the future
    return { type: 'relative_path', value: trimmed_term, title: trimmed_term };
  }

  // 8. Fallback: Not a recognized link type
  return null;
}


/**========================================================================
 * 
 * Creates a markdown link based on the link type.
 * @param {string} text - The text to display for the link.
 * @param {string} link - The actual link (URL, email, or phone number).
 * @param {'weblink'|'anchor'|'email'|'phone'} type - The type of link, as determine_link_type names it.
 * @returns {string|null} - The formatted markdown link, or null for a type that is not linked.
 *
 *========================================================================**/
export function create_markdown_link(text, link, type) {
  switch (type) {
    case 'weblink':
    case 'anchor':
    case 'email':
    case 'phone':
      // NOTE: link already carries its scheme (https:/mailto:/tel:) from determine_link_type
      return `[${text}](${link})`;
    default:
      return null;
  }
}

/**========================================================================
 * 
 * Inserts text at the current cursor position or replaces the current selection.
 * @param {string} text - The text to insert.
 * @param {Range} range - The current selection range.
 * @param {Selection} selection - The current selection object.
 * 
 *========================================================================**/
export function insert_text_at_cursor(text, range, selection) {
  range.deleteContents();
  const text_node = document.createTextNode(text);
  range.insertNode(text_node);
  range.setStartAfter(text_node);
  range.setEndAfter(text_node);
  selection.removeAllRanges();
  selection.addRange(range);
}

const re_unescaped_html = /[&<>"']/g;
const html_entities = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};
export function escape_html(string) {
  string = String(string);
  return string && re_unescaped_html.test(string)
    ? string.replace(re_unescaped_html, (match) => html_entities[match])
    : string;
}

const re_unescaped_html_outside_entities = /&(?!(?:#\d{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,31});)|[<>"']/g;

/**
 * Escapes text for markup like escape_html, but leaves entity references as they are: `&shy;` stays
 * a hyphenation hint and `&amp;` stays a single ampersand. Unlike escape_html it can run over text
 * that is already escaped without doubling it — a link inside a list item passes through both
 * formatters. Only for element content: an entity still decodes, so a URL has to be decoded,
 * checked and run through escape_html instead.
 * @param {string} string - The text to escape.
 * @returns {string} - The text without markup, its entity references intact.
 */
function escape_html_keeping_entities(string) {
  return String(string).replace(re_unescaped_html_outside_entities, (match) => html_entities[match]);
}

/**
 * The entities that actually turn up in authored copy: the five markup-critical ones, the spacing
 * and line-break hints German authors reach for, and the punctuation a word processor substitutes
 * on paste. Everything else stays written as it is — a plain-text field is not a place to grow a
 * full HTML5 entity table.
 */
const named_html_entities = {
  amp: '&', lt: '<', gt: '>', quot: '"', apos: "'",
  nbsp: '\u00a0', shy: '\u00ad', ensp: '\u2002', emsp: '\u2003', thinsp: '\u2009',
  zwnj: '\u200c', zwj: '\u200d',
  ndash: '–', mdash: '—', hellip: '…', bull: '•', middot: '·', times: '×', minus: '−',
  laquo: '«', raquo: '»', bdquo: '„', ldquo: '“', rdquo: '”', sbquo: '‚', lsquo: '‘', rsquo: '’',
  deg: '°', euro: '€', copy: '©', reg: '®', trade: '™',
  szlig: 'ß', auml: 'ä', ouml: 'ö', uuml: 'ü', Auml: 'Ä', Ouml: 'Ö', Uuml: 'Ü',
};

const html_entity_reference = /&(#\d{1,7}|#[xX][0-9a-fA-F]{1,6}|[a-zA-Z][a-zA-Z0-9]{1,31});/g;

/**========================================================================
 *
 * Decodes HTML entities that authors typed into a text field.
 *
 * A title travels through two very different renderers: as block content it ends up inside
 * `{@html}`, where `&shy;` does its job as a hyphenation hint, but the page title and the meta tags
 * are plain text, where the same seven characters show up verbatim. Anything leaving the rich text
 * world for a plain-text destination is decoded here first.
 *
 * Decoding happens in a single pass, so double-encoded input (`&amp;shy;`) yields `&shy;` rather
 * than collapsing all the way. The result is plain text: it carries `<` and `&` unescaped and must
 * be inserted as a text node or an attribute value, never through `{@html}`.
 *
 * @param {string} text - The text possibly containing entity references.
 * @returns {string} - The text with known entities replaced by their characters.
 *
 *========================================================================**/
export function decode_html_entities(text = '') {
  if (!text || !text.includes('&')) return text || '';

  return text.replace(html_entity_reference, (reference, body) => {
    if (body[0] !== '#') return named_html_entities[body] ?? reference;

    const is_hex = body[1] === 'x' || body[1] === 'X';
    const code_point = Number.parseInt(is_hex ? body.slice(2) : body.slice(1), is_hex ? 16 : 10);
    // Lone surrogates and out-of-range code points have no character to become; leave them written.
    if (!Number.isFinite(code_point) || code_point > 0x10ffff) return reference;
    if (code_point >= 0xd800 && code_point <= 0xdfff) return reference;
    return String.fromCodePoint(code_point);
  });
}

/**========================================================================
 *
 * Removes the invisible characters that only mean something inside wrapping body copy: soft
 * hyphens, zero width spaces and joiners, word joiners and a stray byte order mark.
 *
 * They are hints for a line break that a browser tab, a search result snippet or a social card
 * never performs, and they survive copy and paste to confuse whoever pastes the title somewhere
 * else.
 *
 * @param {string} text - The text to clean.
 * @returns {string} - The text without invisible formatting characters.
 *
 *========================================================================**/
export function strip_invisible_characters(text = '') {
  if (!text) return text || '';
  return text.replace(/[\u00ad\u200b\u200c\u200d\u2060\ufeff]/g, '');
}

/**========================================================================
 *
 * Turns an authored rich text string into the plain text a label, an option list, a table cell or
 * a placeholder needs: micro markdown annotations come off and the entities authors type for
 * typography and hyphenation become their characters.
 *
 * Both steps are needed because such a string is written for `{@html}` rendering, where `**bold**`
 * and `&shy;` both do something. Interpolated as text without this, the reader sees the asterisks
 * and the seven characters of the entity instead.
 *
 * Soft hyphens survive on purpose — wrapping text is exactly where they help. Metadata that never
 * wraps (a page title, a mail subject) runs the result through strip_invisible_characters as well.
 *
 * @param {string} text - The authored text.
 * @returns {string} - The text as plain text.
 *
 *========================================================================**/
export function to_plain_text(text = '') {
  return decode_html_entities(render_micro_markdown(text || '', 'ignore-all'));
}

/**========================================================================
 *
 * Checks whether a range sits inside the label portion of an existing "[label](url)"
 * markdown link within content_ref, e.g. to avoid nesting a new link inside it on paste.
 * @param {HTMLElement} content_ref - The reference to the content element.
 * @param {Range} range - The range to check (e.g. the current selection's range).
 * @returns {boolean}
 *
 *========================================================================**/
export function is_selection_inside_markdown_link_label(content_ref, range) {
  if (!content_ref || !range) return false;

  const text_before_range = document.createRange();
  text_before_range.selectNodeContents(content_ref);
  text_before_range.setEnd(range.startContainer, range.startOffset);

  const text_after_range = document.createRange();
  text_after_range.selectNodeContents(content_ref);
  text_after_range.setStart(range.endContainer, range.endOffset);

  const text_before = text_before_range.toString();
  const text_after = text_after_range.toString();

  // Text before must end with an unclosed "[", and text after must close that label
  // with "](url)" before any other bracket interrupts it.
  return /\[[^[\]]*$/.test(text_before) && /^[^[\]]*\]\([^()\n]*\)/.test(text_after);
}

/**========================================================================
 *
 * Handles paste events for editable elements, converting URLs to markdown links when appropriate.
 * @param {ClipboardEvent} event - The paste event.
 * @param {HTMLElement} content_ref - The reference to the content element.
 *
 *========================================================================**/
export function handle_paste_and_convert_urls(event, content_ref) {

  event.preventDefault();
  // Important: we need to use stopPropagation, otherwise the native event will also trigger and the copied text will be inserted twice
  event.stopPropagation();

  const is_editable = content_ref?.isContentEditable || 
                      (content_ref?.tagName === 'INPUT' && content_ref?.type === 'text') || 
                      content_ref?.tagName === 'TEXTAREA';
  if (!is_editable) return;

  // NOTE: With trim_whitespace we remove any leading or trailing whitespaces from the pasted text
  // TODO: We do not want to remove trailing whitespaces in case behind the caret position there's additional text that's not whitespace/ new lines
  let pasted_text = trim_whitespaces((event.clipboardData || window.clipboardData).getData("text"));

  if(!pasted_text) return;

  // HACK: We remove any double newlines, as they are not needed and can cause issues
  // NOTE: Deactivated this hack, as it's really a Chrome issue we can't get around. E.g. if you continue editing the text and add two newlines, you'll again
  // end up with 3 newlines in Chrome. So let's better do nothing and accept the flaw.
  // pasted_text = pasted_text.replace(/\n{2,}/g, '\n');
  
  const selection = window.getSelection();
  if (!selection.rangeCount) return;

  const selected_text = selection.toString();

  // NOTE: The editable content shows raw markdown as plain text (e.g. "[label](url)"), so
  // pasting a link on top of text that's already inside an existing link's label would nest
  // a new markdown link inside it (e.g. "[[label](mailto:new)](mailto:old)"). Detect that case
  // and fall back to a plain text paste instead of auto-linking.
  const is_inside_markdown_link_label = is_selection_inside_markdown_link_label(content_ref, selection.getRangeAt(0));

  const link_details = is_inside_markdown_link_label ? null : determine_link_type(pasted_text);
  const type = link_details?.type;
  const value = link_details?.value;

  let inserted_text;
  if (selected_text && type) {
    // The selection becomes the label; a type that is not linked keeps the paste as typed
    inserted_text = create_markdown_link(selected_text, value, type) ?? pasted_text;
  } else if (type && type !== 'anchor') {
    // A bare `#word` pasted into text is far more often a hashtag than a block link
    inserted_text = create_markdown_link(pasted_text, value, type) ?? pasted_text;
  } else {
    inserted_text = pasted_text;
  }

  // Instead of removing newline characters we now preserve them by:
  // 1. Escaping the text for safety
  // 2. Converting newline characters to <br> tags so that the browser renders line breaks
  // This method prevents the browser from inserting text wrapped in <div> elements.
  inserted_text = escape_html(inserted_text).replace(/\r?\n/g, '<br>');
  document.execCommand('insertHTML', false, inserted_text);

  // Note: although execCommand is markeed as deprecated, there's not really an alternative to it in 2024, an all browsers are forced to continue support it
  // So we don't need to worry about the fallback, and can just use execCommand
  // (see commit f999adc79653f40c667e8793bd1ecbf6ef7208b1 from 27. June 2024 for previous version with fallback)
  // learn more here:
  // https://stackoverflow.com/questions/60581285/execcommand-is-now-obsolete-whats-the-alternative

  // Important: Trigger an input event to ensure Svelte updates the bound value
  // without this trigger the DOM will update and show the new text, but the bound value will not be updated.
  content_ref.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Checks if a user role meets or exceeds the minimum required access level.
 * @param {string} min_access_level - The minimum access level required ('admin', 'member', 'contact', 'public').
 * @param {string} [user_role='public'] - The role of the user, defaults to 'public' if not provided or if false/undefined.
 * @returns {boolean} - True if the user role meets or exceeds the minimum access level, false otherwise.
 * @example
 * has_access('member', 'admin');
 * → will return true
 */
export function has_access(min_access_level, user_role) {
  user_role = user_role || 'public'; // Ensure default to 'public' if user_role is false or undefined

  // Validate input types
  if (typeof min_access_level !== 'string') {
    console.error('Invalid input types to has_access function: expected strings.');
    return false;
  }

  // Retrieve access levels from roles
  const user_access_level = ROLES_AND_ACCESS_LEVELS.find(role => role.role === user_role)?.level;
  const required_access_level = ROLES_AND_ACCESS_LEVELS.find(role => role.role === min_access_level)?.level;

  // Check for undefined access levels
  if (user_access_level === undefined || required_access_level === undefined) {
    console.error('Invalid role or minimum access level provided.');
    return false;
  }

  // Compare access levels
  return user_access_level >= required_access_level;
}

/**
 * Checks if a user has access to a specific entry.
 * @param {Object} entry - The entry to check access for.
 * @param {Object} current_user - The current user object.
 * @returns {boolean} - Whether the user has access to the entry.
 */
export function has_access_to_entry(entry, current_user) {
  if (!entry) return false;

  const user_role = current_user?.role || 'public';
  const allowed_visibilities = ROLE_VISIBILITY_ACCESS[user_role] || ['public'];

  if (entry.visibility === 'private') {
    return entry.user_id === current_user?.user_id || current_user?.role === 'admin';
  }
  return allowed_visibilities.includes(entry.visibility);
}

/**
 * Checks if a user has access to an entry preview.
 * Preview policy:
 * - public and member previews are public
 * - internal previews are admin-only
 * - private previews are owner/admin only
 * @param {Object} entry - The entry to check preview access for.
 * @param {Object} current_user - The current user object.
 * @returns {boolean} - Whether the user has access to the entry preview.
 */
export function has_access_to_entry_preview(entry, current_user) {
  if (!entry) return false;

  const user_role = current_user?.role || 'public';
  const allowed_preview_visibilities = ROLE_PREVIEW_VISIBILITY_ACCESS[user_role] || ['public'];

  if (entry.visibility === 'private') {
    return entry.user_id === current_user?.user_id || current_user?.role === 'admin';
  }

  return allowed_preview_visibilities.includes(entry.visibility);
}

/**
 * Wraps the currently selected text in an editable element with specified wrapper characters.
 * 
 * @param {HTMLElement} element - The editable element (contenteditable div, textarea, or input)
 * @param {string} wrapper - The string to wrap around the selected text (e.g., '**' for bold)
 */
export function wrap_selected_text(element, wrapper) {
  const selection = window.getSelection();
  if (!selection.rangeCount) return;

  const range = selection.getRangeAt(0);
  const selected_text = range.toString();

  if (selected_text) {
    const wrapped_text = `${wrapper}${selected_text}${wrapper}`;    
    document.execCommand('insertText', false, wrapped_text);
    // Update the content binding
    element.dispatchEvent(new Event('input', { bubbles: true }));
  }
}



/**
 * Finds the closest predefined aspect ratio to the average of given aspect ratios.
 *
 * @param {number[]} source_aspect_ratios - An array of aspect ratios of the source image(s).
 * @param {string[]} target_aspect_ratios - An array of predefined aspect ratios that should be applied.
 * @returns {string|undefined} The closest predefined aspect ratio as a string, or undefined if inputs are invalid.
 */
export function find_closest_aspect_ratio_for_brand(source_aspect_ratios, target_aspect_ratios) {
  if (!source_aspect_ratios || source_aspect_ratios.length === 0 || !target_aspect_ratios || target_aspect_ratios.length === 0) {
    return undefined;
  }

  const average_aspect_ratio = source_aspect_ratios.reduce((sum, ratio) => sum + ratio, 0) / source_aspect_ratios.length;

  return target_aspect_ratios.reduce((closest, current) => {
    const current_float = convert_string_to_float(current);
    const closest_float = convert_string_to_float(closest);
    return Math.abs(current_float - average_aspect_ratio) < Math.abs(closest_float - average_aspect_ratio) ? current : closest;
  });
}


// Slug parsing lives in slugs.js, without imports: src/params.js needs it, and SvelteKit loads
// that file with plain Node at build time, where this module's $app imports don't resolve
export { get_entry_id_and_title_from_slug, get_user_id_and_name_from_slug } from '#lib/slugs.js';

export function get_user_slug(user_id, name) {
  const slugified_name = slugify(name, {
    lower: true,
    strict: true,
    locale: LANG, // important for e.g. "Umlaute (ä --> ae, ö --> oe, ü --> ue, ß --> ss)"
  });

  return `@${slugified_name}-${user_id}`;
}

/**
 * Creates a message link with pre-filled message text.
 * 
 * @param {string} message_text - The text to pre-fill in the message.
 * @param {string} [base_path='/messages'] - The base path for the message link.
 * @returns {string} The generated message link.
 */
export function create_message_link(message_text, base_path = '/messages') {
  const encoded_message_text = encodeURIComponent(message_text);
  return `${base_path}?text=${encoded_message_text}`;
}


/**
 * Helper function to check if user has permission for operation on entry type
 * @param {string} entry_type - The type of entry (e.g. 'collection', 'page', 'event', 'image')
 * @param {string} operation - The operation to check ('create', 'update', 'delete')
 * @param {string} user_role - The role of the user ('admin', 'member', etc)
 * @returns {boolean} Whether the user has permission for the operation
 */
export function has_entry_access(entry_type, operation, user_role) {
  if (!ENTRY_ACCESS_CONTROL[entry_type]) {
    return false;
  }
  return ENTRY_ACCESS_CONTROL[entry_type][operation]?.includes(user_role) || false;
}

/**
 * Helper function to check if user has permission to access a route
 * @param {string} route_name - The name of the route (e.g. 'users', 'settings', 'messages')
 * @param {string} user_role - The role of the user ('admin', 'member', etc)
 * @returns {boolean} Whether the user has permission to access the route
 */
export function has_route_access(route_name, user_role) {
  // If route is not in ROUTE_ACCESS_CONTROL, it's publicly accessible
  if (!ROUTE_ACCESS_CONTROL[route_name]) {
    return true;
  }
  return ROUTE_ACCESS_CONTROL[route_name].includes(user_role) || false;
}

/**
 * Payment state of one registration row as admins see it: 'pending' (Stripe
 * checkout still open), 'processing' (checkout closed, payment settling —
 * nothing may be booked on top of it), 'refunded', 'paid', 'comp'
 * (complimentary seat), 'unpaid' (seat confirmed, money expected) or 'free'
 * (nothing owed: the event has no price, or the row holds no seat — a
 * leftover waitlist row of an event that became paid).
 */
export function get_registration_payment_state(registration, event) {
  if (registration.status === 'waitlist') return 'free';
  if (registration.status === 'pending') return registration.stripe_checkout_url ? 'pending' : 'processing';
  if (registration.status === 'refunded') return 'refunded';
  if (registration.payment_method === 'comp') return 'comp';
  if (registration.paid_at) return 'paid';
  return event?.payment_required ? 'unpaid' : 'free';
}

/**
 * Generates a CSV file containing event registration data.
 * @param {Array<Object>} registrations - Array of registration objects
 * @param {Object} event - The event entry (title, dates, price)
 * @param {Function} t - Translation function
 * @returns {{ blob: Blob, filename: string }} CSV contents and a filename
 */
export function generate_registrations_csv(registrations, event, t) {
  const event_date = format_date_range(event.starts_at, event.ends_at, event.all_day);
  const has_payments = event.payment_required === true;
  const headers = [
    t('name'),
    t('email'),
    t('created_at'),
    t('updated_at'),
    t('id'),
    t('event'),
    t('event_date'),
    t('waitlist'),
    ...(has_payments ? [t('payment'), t('payment_method'), t('amount'), t('paid_at')] : []),
    t('notes')
  ];

  /**
   * Escapes a value for safe CSV embedding: doubles internal quotes per RFC 4180,
   * and prefixes formula-triggering characters to prevent spreadsheet injection.
   */
  function csv_safe(val) {
    let str = String(val ?? '');
    if (/^[=+\-@\t\r]/.test(str)) str = "'" + str;
    return '"' + str.replace(/"/g, '""') + '"';
  }

  let csv_lines = [];
  csv_lines.push(headers.join(','));
  for (let i = 0, len = registrations.length; i < len; i++) {
    const reg = registrations[i];
    const created = reg.created_at ? format_date(reg.created_at, 'DMYHHm') : '';
    const updated = reg.updated_at ? format_date(reg.updated_at, 'DMYHHm') : '';
    
    const payment_state = get_registration_payment_state(reg, event);
    const payment_columns = has_payments ? [
      csv_safe(t(`payment_state_${payment_state}`)),
      csv_safe(reg.payment_method ? t(`payment_method_${reg.payment_method}`) : ''),
      csv_safe(Number.isInteger(reg.amount_paid) ? (reg.amount_paid / 100).toFixed(2) : ''),
      csv_safe(reg.paid_at ? format_date(reg.paid_at, 'DMYHHm') : ''),
    ] : [];

    csv_lines.push([
      csv_safe(reg.name),
      csv_safe(reg.email),
      csv_safe(created),
      csv_safe(updated),
      csv_safe(reg.event_registration_id),
      csv_safe(event.title),
      csv_safe(event_date),
      csv_safe(reg.status === 'waitlist'),
      ...payment_columns,
      csv_safe(reg.notes ?? ''),
    ].join(','));
  }

  const csv_content = csv_lines.join('\n');

  return {
    blob: new Blob([csv_content], { type: 'text/csv' }),
    filename: `${event.title}-${t('registrations')}.csv`
  };
}

// see spacing.css for the mapping
const SIZE_MAP_REM = {
  '0': 0,
  '05': 0.0625,
  '1': 0.125,
  '2': 0.25,
  '3': 0.5,
  '4': 0.75,
  '5': 1,
  '6': 1.25,
  '7': 1.5,
  '8': 1.75,
  '9': 2,
  '10': 2.25,
  '11': 2.5,
  '12': 2.75,
  '14': 3,
  '16': 3.5,
  '20': 4,
  '24': 5,
  '28': 6,
  '32': 7,
  '36': 8,
  '40': 9,
  '44': 10,
  '48': 11,
  '52': 12,
  '56': 13,
  '60': 14,
  '64': 15,
  '72': 16,
  '80': 18,
  '96': 20,
};

/**
 * Converts a size variable name string (e.g., '--size-10') into a pixel value
 * based on a predefined mapping derived from spacing.css. Works server-side.
 *
 * @param {string} size_variable_name - The size variable name (e.g., '--size-10').
 * @param {number} [base_font_size=16] - The base font size in pixels for rem conversion.
 * @returns {number|null} The calculated pixel value, or null if the name is invalid or not found.
 */
export function size_var_to_px(size_variable_name, base_font_size = 16) {
  const size_suffix = size_variable_name?.match(/--size-(.*)/)?.[1];
  const rem_value = size_suffix !== undefined ? SIZE_MAP_REM[size_suffix] : undefined;
  return rem_value !== undefined ? rem_value * base_font_size : null;
}

// generate_font_faces
export function generate_font_faces(font_specs) {
  const font_face_rules = [];

  // Iterate over each font family spec in the font_specs object
  Object.values(font_specs).forEach(family_spec => {
    const { folder_name, family_name, font_faces } = family_spec;

    // Iterate over each font face within the family
    font_faces.forEach(font_face_spec => {
      // Assumption: font_face_spec contains a 'file_name' property
      if (!font_face_spec.file_name) {
        console.warn(`Font face spec for ${family_name} is missing 'file_name'. Skipping @font-face generation for this variant.`);
        return; // Skip if file_name is missing
      }

      const font_pack_file_path = `${folder_name}/${font_face_spec.file_name}`;
      const src_url = `/assets/fonts/${font_pack_file_path}`;

      if (font_face_spec.font_file_type === 'static') {
        const weight = font_face_spec.weight ?? 400; // Default to 400 if not specified
        const font_style = font_face_spec.style === 'italic' ? 'italic' : 'normal';

        font_face_rules.push([
          `@font-face {`,
          `  font-family: '${family_name}';`,
          `  src: url('${src_url}') format('woff2');`,
          `  font-weight: ${weight};`,
          `  font-style: ${font_style};`,
          `  font-display: swap;`,
          `}`
        ].join('\n'));

      } else if (font_face_spec.font_file_type === 'variable') {
        // Determine weight range, default to 'normal' if axis not present
        const weight_axis = font_face_spec.variable_axes?.wght;
        const weight_range = weight_axis ? `${weight_axis.min} ${weight_axis.max}` : 'normal';

        // Determine style: prioritize slant axis, then italic style, fallback to normal
        const slant_axis = font_face_spec.variable_axes?.slnt;
        let font_style = 'normal';
        if (slant_axis) {
          font_style = `oblique ${slant_axis.min}deg ${slant_axis.max}deg`;
        } else if (font_face_spec.style === 'italic') {
          // Check for boolean italic if slant axis isn't defined (some variable fonts use 'ital' axis or just style flag)
          font_style = 'italic'; 
        }

        font_face_rules.push([
          `@font-face {`,
          `  font-family: '${family_name}';`,
          `  src: url('${src_url}') format('woff2');`,
          `  font-weight: ${weight_range};`,
          `  font-style: ${font_style};`,
          `  font-display: swap;`,
          `}`
        ].join('\n'));
      }
    });
  });

  return font_face_rules.join('\n\n');
}

/**
 * Extracts unique font family names used in site settings typography styles
 * @param {Object} site_settings - Site settings object containing typography styles
 * @param {Object} site_settings.typography - Typography settings
 * @param {Object} site_settings.typography.styles - Typography style definitions
 * @returns {string[]} Array of unique font family names
 */
export function get_font_families_used_in_site_settings(site_settings) {
  const font_specs = Object.values(site_settings.typography.styles).map(style => style.font_family);
  return [...new Set(font_specs)];
}

/**
 * Filters font specs to only include those with matching family names
 * @param {Object[]} font_specs - Array of font specification objects
 * @param {string[]} [font_family_names=[]] - Array of font family names to filter by
 * @returns {Object[]} Filtered array of font specifications
 */
export function get_filtered_font_specs(font_specs, font_family_names = []) {
  if (font_family_names.length === 0) {
    return font_specs;
  }
  return Object.values(font_specs).filter(font_spec => font_family_names.includes(font_spec.family_name));
}


/**========================================================================
 * 
 * Color
 * Color utility functions for contrast and color space conversions
 * 
*==========================================================================*/

/**========================================================================
 *
 * Constructs an OKLCH color string from an color object representation.
 * OKLCH: L - Lightness (0 to 1), C - Chroma (0 to ~0.4), H - Hue (0 to 360), A - Alpha (0 to 1)
 * Output Format: oklch(L C H / A) - L and A are decimals 0-1, A is optional
 *
 * @param {{ l: number, c: number, h: number, a?: number }} obj - An object containing the lightness, chroma, hue and alpha color components.
 * @returns {string} - The OKLCH color string.
 *
 *========================================================================**/
export const format_oklch = ({ l, c, h, a }) => {
  // Clamp values to their theoretical/practical ranges
  const clamped_l = Math.max(0, Math.min(1, l)); // L as 0-1 decimal
  const clamped_c = Math.max(0, c); // Chroma can technically exceed 0.4
  const clamped_h = h >= 0 ? h % 360 : ((h % 360) + 360); // Normalize hue to 0-360 inclusive
  const clamped_a = (a !== undefined && a !== null && a >= 0 && a < 1) ? Math.max(0, Math.min(1, a)) : undefined; // Clamp alpha 0-1

  // Format with fixed precision to avoid overly long strings
  const l_str = clamped_l.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''); // Keep L as decimal, remove trailing zeros
  const c_str = clamped_c.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, ''); // Remove trailing zeros after decimal
  const h_str = clamped_h.toFixed(2).replace(/\.00$/, '').replace(/\.$/, ''); // Remove .00 or trailing .

  let alpha_str = '';
  if (clamped_a !== undefined) {
    // Format alpha nicely, remove trailing zeros, remove leading 0 if it's 0.x
    const a_str = clamped_a.toFixed(2).replace(/(\.\d*?)0+$/, '$1').replace(/^0\./, '.').replace(/\.$/, ''); 
    // Ensure alpha is not just '.' if value was 0.0
    alpha_str = ` / ${a_str === '.' ? '0' : a_str}`;
  }

  return `oklch(${l_str} ${c_str} ${h_str}${alpha_str})`;
};


/**========================================================================
 *
 * Parses an OKLCH color string into its components, accepting various valid CSS formats.
 * OKLCH: L - Lightness, C - Chroma, H - Hue, A - Alpha
 * Accepted Input Formats (per MDN):
 *   L: <number> (0-1), <percentage> (0%-100%), none
 *   C: <number> (>=0), <percentage> (0%-100%, maps to 0-0.4), none
 *   H: <number> (0-360), <angle> (deg, rad, grad, turn), none
 *   A: <number> (0-1), <percentage> (0%-100%), none (defaults to 1)
 *
 * Output Format (LCHA object):
 *   { l: number (0-1), c: number (>=0), h: number (0-360), a: number (0-1) }
 *
 * @param {string} oklch_string - The OKLCH color string to parse.
 * @returns {{ l: number, c: number, h: number, a: number } | null} - An object with l, c, h, a or null if parsing fails.
 *
 *========================================================================**/
export function parse_oklch(oklch_string) {
  if (!oklch_string || typeof oklch_string !== 'string') return null;

  // More complex regex to capture different units and keywords
  const match = oklch_string.trim().match(
    /^oklch\(\s*([\d.]+%?|none)\s+([\d.]+%?|none)\s+([\d.]+(?:deg|rad|grad|turn)?|none)(?:\s*\/\s*([\d.]+%?|none))?\s*\)$/i
  );

  if (!match) return null;

  try {
    let [, l_str, c_str, h_str, a_str] = match;

    // --- Parse L --- 
    let l = 0;
    if (l_str.toLowerCase() !== 'none') {
      l = parseFloat(l_str);
      if (l_str.endsWith('%')) l /= 100;
    }
    l = Math.max(0, Math.min(1, l)); // Clamp L to 0-1

    // --- Parse C --- 
    let c = 0;
    if (c_str.toLowerCase() !== 'none') {
      c = parseFloat(c_str);
      // Convert percentage (100% = 0.4)
      if (c_str.endsWith('%')) c *= 0.004;
    }
    c = Math.max(0, c); // Clamp C >= 0

    // --- Parse H --- 
    let h = 0;
    if (h_str.toLowerCase() !== 'none') {
      const hue_match = h_str.match(/^([\d.]+)(deg|rad|grad|turn)?$/i);
      if (hue_match) {
        h = parseFloat(hue_match[1]);
        const unit = hue_match[2]?.toLowerCase();
        if (unit === 'rad') h *= (180 / Math.PI);
        else if (unit === 'grad') h *= (360 / 400);
        else if (unit === 'turn') h *= 360;
        // 'deg' or no unit is already degrees
      }
    }
    h = h >= 0 ? h % 360 : (h % 360 + 360); // Normalize hue to 0-360

    // --- Parse A (optional) --- 
    let a = 1; // Default alpha is 1 (opaque)
    if (a_str !== undefined && a_str.toLowerCase() !== 'none') {
      a = parseFloat(a_str);
      if (a_str.endsWith('%')) a /= 100;
    }
    // Note: If a_str is 'none', alpha remains 1 (fully opaque, as per CSS defaults)
    a = Math.max(0, Math.min(1, a)); // Clamp A to 0-1

    // Final validation
    if (isNaN(l) || isNaN(c) || isNaN(h) || isNaN(a)) return null;

    return { l, c, h, a };
  } catch (e) {
    console.error("Failed to parse OKLCH string:", oklch_string, e);
    return null;
  }
}


/**
 * Convert OKLCH to RGB
 * @param {number} L - Lightness (0-1)
 * @param {number} c - Chroma
 * @param {number} h - Hue (0-360)
 * @param {number} [alpha=1] - Alpha value (0-1)
 * @returns {[number, number, number, number]} RGBA array
 */
export function oklch_to_rgb(L, c, h, alpha = 1) {
  // Convert OKLCH to OKLab
  const a = c * Math.cos(h * Math.PI / 180);
  const b = c * Math.sin(h * Math.PI / 180);
  
  // Convert OKLab to RGB
  const l = L + 0.3963377774 * a + 0.2158037573 * b;
  const m = L - 0.1055613458 * a - 0.0638541728 * b;
  const s = L - 0.0894841775 * a - 1.2914855480 * b;

  const l_3 = l * l * l;
  const m_3 = m * m * m;
  const s_3 = s * s * s;

  const r = +4.0767416621 * l_3 - 3.3077115913 * m_3 + 0.2309699292 * s_3;
  const g = -1.2684380046 * l_3 + 2.6097574011 * m_3 - 0.3413193965 * s_3;
  const b_val = -0.0041960863 * l_3 - 0.7034186147 * m_3 + 1.7076147010 * s_3;

  // Convert to sRGB and clamp
  const to_srgb = (x) => {
      if (x <= 0) return 0;
      if (x >= 1) return 255;
      return Math.round((x >= 0.0031308 ? 1.055 * Math.pow(x, 1/2.4) - 0.055 : 12.92 * x) * 255);
  };

  return [to_srgb(r), to_srgb(g), to_srgb(b_val), alpha];
}

/**
 * Convert RGB values to hexadecimal color string
 * @param {[number, number, number]} rgb_array - RGB color array [r, g, b] where each value is 0-255
 * @returns {string} Hexadecimal color string (e.g., "#ff0000")
 */
export function rgb_to_hex([r, g, b]) {
  const to_hex = (n) => n.toString(16).padStart(2, '0');
  return `#${to_hex(r)}${to_hex(g)}${to_hex(b)}`;
}

/**
 * Convert OKLCH string to hexadecimal color string
 * @param {string} oklch_string - OKLCH color string (e.g., "oklch(50% 0.2 240)")
 * @param {string} fallback - Fallback color string (e.g., "#000000")
 * @returns {string} Hexadecimal color string (e.g., "#ff0000")
 */
export function oklch_string_to_hex(oklch_string, fallback = '#000000') {
  try {
    const lcha = parse_oklch(oklch_string);
    if (!lcha) return fallback;
    const [r, g, b] = oklch_to_rgb(lcha.l, lcha.c, lcha.h);
    return rgb_to_hex([r, g, b]);
  } catch (_) {
    return fallback;
  }
}

/**
 * Convert hexadecimal color string to OKLCH string
 * @param {string} hex - Hexadecimal color string (e.g., "#ff0000" or "ff0000")
 * @param {string} [fallback] - Fallback OKLCH string if conversion fails
 * @returns {string} OKLCH color string (e.g., "oklch(0.6279 0.2577 29.23)")
 */
export function hex_to_oklch(hex, fallback = 'oklch(0 0 0)') {
  try {
    // Normalize hex
    const normalized = hex.startsWith('#') ? hex : `#${hex}`;
    
    // Support both 6-char (#rrggbb) and 8-char (#rrggbbaa) hex
    const match6 = normalized.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    const match8 = normalized.match(/^#([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    
    if (!match6 && !match8) return fallback;
    
    const match = match8 || match6;
    const r = parseInt(match[1], 16) / 255;
    const g = parseInt(match[2], 16) / 255;
    const b = parseInt(match[3], 16) / 255;
    const alpha = match8 ? parseInt(match[4], 16) / 255 : null;
    
    // Convert sRGB to linear RGB
    const to_linear = (c) => c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    const lr = to_linear(r);
    const lg = to_linear(g);
    const lb = to_linear(b);
    
    // Convert linear RGB to OKLab
    const l_ = Math.cbrt(0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb);
    const m_ = Math.cbrt(0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb);
    const s_ = Math.cbrt(0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb);
    
    const L = 0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_;
    const a = 1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_;
    const b_val = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_;
    
    // Convert OKLab to OKLCH
    const C = Math.sqrt(a * a + b_val * b_val);
    let H = Math.atan2(b_val, a) * 180 / Math.PI;
    if (H < 0) H += 360;
    
    // Format with reasonable precision
    const l_str = L.toFixed(4).replace(/\.?0+$/, '');
    const c_str = C.toFixed(4).replace(/\.?0+$/, '');
    const h_str = C < 0.0001 ? '0' : H.toFixed(2).replace(/\.?0+$/, ''); // Hue is meaningless for achromatic colors
    
    // Include alpha if present and less than 1
    if (alpha !== null && alpha < 1) {
      const a_str = alpha.toFixed(2).replace(/\.?0+$/, '');
      return `oklch(${l_str} ${c_str} ${h_str} / ${a_str})`;
    }
    
    return `oklch(${l_str} ${c_str} ${h_str})`;
  } catch (_) {
    return fallback;
  }
}


/**
* Convert sRGB to Y (luminance) for APCA
* @param {[number, number, number]} srgb - sRGB color array [r,g,b]
* @returns {number} Y value (luminance)
*/
export function sRGB_to_Y(srgb) {
  const r = Math.pow(srgb[0] / 255, 2.4);
  const g = Math.pow(srgb[1] / 255, 2.4);
  const b = Math.pow(srgb[2] / 255, 2.4);
  let y = 0.2126729 * r + 0.7151522 * g + 0.0721750 * b;

  if (y < 0.022) {
      y += Math.pow(0.022 - y, 1.414);
  }
  return y;
}

/**
* Calculate APCA contrast between two colors
* @param {[number, number, number]} fg - Foreground RGB color [r,g,b]
* @param {[number, number, number]} bg - Background RGB color [r,g,b]
* @returns {number} APCA contrast value
*/
export function get_apca_contrast(fg, bg) {
  const yfg = sRGB_to_Y(fg);
  const ybg = sRGB_to_Y(bg);
  let c = 1.14;

  if (ybg > yfg) {
      c *= Math.pow(ybg, 0.56) - Math.pow(yfg, 0.57);
  } else {
      c *= Math.pow(ybg, 0.65) - Math.pow(yfg, 0.62);
  }

  if (Math.abs(c) < 0.1) {
      return 0;
  } else if (c > 0) {
      c -= 0.027;
  } else {
      c += 0.027;
  }

  return c * 100;
}

/**
 * Check if contrast meets minimum requirements for 16px text
 * @param {number} contrast - APCA contrast value
 * @param {number} fontWeight - Font weight (400-700)
 * @returns {boolean} Whether the contrast meets requirements
 */
export function meets_contrast_requirements(contrast, fontWeight = 400) {
    // For 16px text, the minimum contrast requirements are:
    // 400: 90, 500: 75, 600: 70, 700: 60
    const thresholds = {
        400: 90,
        500: 75,
        600: 70,
        700: 60
    };
    
    // Find the closest font weight threshold
    const availableWeights = Object.keys(thresholds).map(Number).sort((a, b) => a - b);
    const closestWeight = availableWeights.reduce((prev, curr) => 
        Math.abs(curr - fontWeight) < Math.abs(prev - fontWeight) ? curr : prev
    );
    
    const threshold = thresholds[closestWeight];
    const absContrast = Math.abs(contrast);
    
    // For APCA, we need to consider both positive and negative values
    // Negative values indicate light text on dark background
    // Positive values indicate dark text on light background
    // The absolute value should meet the threshold
    return absContrast >= threshold;
}

/**
 * Get a human-readable contrast level description
 * @param {number} contrast - APCA contrast value
 * @param {number} fontWeight - Font weight (400-700)
 * @returns {string} Contrast level description
 */
export function get_contrast_level(contrast, fontWeight = 400) {
    const absContrast = Math.abs(contrast);
    const is_light_text = contrast < 0;
    
    // For 16px text
    if (absContrast >= 90) return is_light_text ? 'Light text on dark: Excellent' : 'Dark text on light: Excellent';
    if (absContrast >= 75) return is_light_text ? 'Light text on dark: Good' : 'Dark text on light: Good';
    if (absContrast >= 60) return is_light_text ? 'Light text on dark: Fair' : 'Dark text on light: Fair';
    if (absContrast >= 45) return is_light_text ? 'Light text on dark: Poor' : 'Dark text on light: Poor';
    return is_light_text ? 'Light text on dark: Fail' : 'Dark text on light: Fail';
}


/**
 * Choose the better contrast text between two candidate text colors
 * @param {Object} bg_lcha - Background OKLCH color object
 * @param {Object} candidate_text_lcha_1 - First candidate text OKLCH color object
 * @param {Object} candidate_text_lcha_2 - Second candidate text OKLCH color object
 * @returns {Object} The better contrast text OKLCH color object
 */
export function choose_better_contrast_text(bg_lcha, candidate_text_lcha_1, candidate_text_lcha_2) {
  try {
    const bg_rgb = oklch_to_rgb(bg_lcha.l, bg_lcha.c, bg_lcha.h);
    const t1_rgb = oklch_to_rgb(candidate_text_lcha_1.l, candidate_text_lcha_1.c, candidate_text_lcha_1.h);
    const t2_rgb = oklch_to_rgb(candidate_text_lcha_2.l, candidate_text_lcha_2.c, candidate_text_lcha_2.h);
    const c1 = Math.abs(get_apca_contrast(t1_rgb.slice(0, 3), bg_rgb.slice(0, 3)));
    const c2 = Math.abs(get_apca_contrast(t2_rgb.slice(0, 3), bg_rgb.slice(0, 3)));
    return c1 >= c2 ? candidate_text_lcha_1 : candidate_text_lcha_2;
  } catch (_) {
    return candidate_text_lcha_1;
  }
}


/**
 * Ensures a path is formatted correctly for a redirect header,
 * handling leading slashes and properly encoding query parameters.
 * @param {string | null | undefined} path_with_query The potential path with query string.
 * @returns {string} A safe path for redirection.
 */
export function encode_redirect_path(path_with_query) {
  if (!path_with_query || path_with_query === '/' || path_with_query.startsWith('//')) {
    return '/';
  }
  
  // Ensure it starts with a slash if it's a relative path potentially missing one
  let path = path_with_query.startsWith('/') ? path_with_query : `/${path_with_query}`;

  // Normalize: best-effort decode to avoid visible %2F etc.; ignore malformed encodings
  try {
    path = decodeURIComponent(path);
  } catch (_err) {
    // ignore
  }

  // Block protocol-relative URLs (open redirect via //example.com)
  if (path.startsWith('//')) {
    return '/';
  }

  const query_index = path.indexOf('?');
  if (query_index === -1) {
    // No query string: validate base-only path (allow safe characters only)
    const is_safe_base_only = /^\/[A-Za-z0-9_\-./]*$/.test(path);
    return is_safe_base_only ? path : '/';
  }

  const base_path = path.substring(0, query_index);
  const query_string = path.substring(query_index + 1);
  
  // Use URLSearchParams to handle encoding of query parameters correctly.
  // It automatically encodes parameter names and values, including newlines etc.
  const params = new URLSearchParams(query_string);
  const encoded_query_string = params.toString();

  // Validate base path
  const is_safe_base = /^\/[A-Za-z0-9_\-./]*$/.test(base_path);
  const safe_base = is_safe_base ? base_path : '/';

  // Avoid trailing '?' when there are no params
  return encoded_query_string ? `${safe_base}?${encoded_query_string}` : safe_base;
}

/**
 * Resolves a post-auth redirect path safely.
 * - Sanitizes the input path using encode_redirect_path
 * - Prevents redirect loops by never returning a path that starts with '/login'
 *
 * @param {string | undefined | null} target_path - desired redirect path (may be relative or missing slash)
 * @returns {string} A safe, non-looping path for redirection
 */
export function resolve_post_auth_redirect_path(target_path) {
  const safe_path = encode_redirect_path(target_path || '/');
  if (safe_path.startsWith('/login')) return '/';
  return safe_path;
}

/**
 * Handle multi-selection with shift click for range selection
 * 
 * @param {string} item_id - The ID of the item being clicked
 * @param {Event} event - The click event (to check for shift key)
 * @param {Array} items - The full list of items
 * @param {Array<string>} selected_ids - Currently selected item IDs
 * @param {string|null} last_selected_id - The last selected item ID (for range selection)
 * @param {Function} get_item_id - Function to extract ID from an item
 * @returns {Object} Object with updated selected_ids and last_selected_id
 */
export function handle_multi_selection(item_id, event, items, selected_ids, last_selected_id, get_item_id) {
  if (event.shiftKey && last_selected_id !== null) {
    const start = items.findIndex(item => get_item_id(item) === last_selected_id);
    const end = items.findIndex(item => get_item_id(item) === item_id);
    const range = items.slice(Math.min(start, end), Math.max(start, end) + 1);
    
    const range_ids = new Set(range.map(get_item_id));
    const is_selecting = !selected_ids.includes(item_id);

    if (is_selecting) {
      selected_ids = [...new Set([...selected_ids, ...range_ids])];
    } else {
      selected_ids = selected_ids.filter(id => !range_ids.has(id));
    }
  } else {
    if (selected_ids.includes(item_id)) {
      selected_ids = selected_ids.filter(id => id !== item_id);
    } else {
      selected_ids = [...selected_ids, item_id];
    }
  }
  
  return { selected_ids, last_selected_id: item_id };
}

export const ALLOWED_CUSTOM_FIELD_TYPES = [
  'text',
  'longtext',
  'float_number',
  'integer_number',
  'checkbox',
  'select',
  'phone',
  'weblink'
];

/**
 * Validates and normalizes custom fields from a FormData submission according to site settings.
 * Does not throw; returns either { custom_fields } or { error: { field, message } }.
 *
 * @param {{ custom_form_fields?: Array<{ id: string, type: string, label?: string, required?: boolean }> }} site_settings
 * @param {FormData} form_data
 * @param {{ enforce_required?: boolean, t?: (key: string, vars?: Record<string,string>) => string }} options
 * @returns {{ custom_fields?: Record<string, any>, error?: { field: string, message: string } }}
 */
export function validate_and_normalize_custom_fields(site_settings, form_data, { enforce_required = true, t = (k) => k } = {}) {
  const custom_fields = {};

  for (const field of site_settings.custom_form_fields || []) {
    if (!ALLOWED_CUSTOM_FIELD_TYPES.includes(field.type)) continue;

    const raw_value = form_data.get(`custom_field_${field.id}`);

    if (field.type === 'checkbox') {
      custom_fields[field.id] = raw_value === 'true';
      continue;
    }

    if (raw_value !== null && raw_value !== '') {
      // Security: validate field ID contains only safe characters
      if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(field.id)) continue;

      const trimmed = String(raw_value).trim();

      if (field.type === 'float_number') {
        const num = Number(trimmed);
        if (Number.isNaN(num)) {
          return { error: { field: field.id, message: t('field_must_be_valid_number', { field: field.label || field.id }) } };
        }
        custom_fields[field.id] = num;
      } else if (field.type === 'integer_number') {
        if (!is_strict_integer(trimmed)) {
          return { error: { field: field.id, message: t('field_must_be_valid_integer', { field: field.label || field.id }) } };
        }
        custom_fields[field.id] = Number.parseInt(trimmed, 10);
      } else if (field.type === 'phone') {
        if (!is_phone(trimmed)) {
          return { error: { field: field.id, message: t('field_must_be_valid_phone_number', { field: field.label || field.id }) } };
        }
        custom_fields[field.id] = trimmed;
      } else if (field.type === 'weblink') {
        const normalized = is_url(trimmed, true);
        if (!normalized) {
          return { error: { field: field.id, message: t('field_must_be_valid_url', { field: field.label || field.id }) } };
        }
        custom_fields[field.id] = normalized;
      } else {
        custom_fields[field.id] = trimmed;
      }
    } else if (enforce_required && field.required) {
      return { error: { field: field.id, message: t('field_is_required', { field: field.label || field.id }) } };
    }
  }

  return { custom_fields };
}


